import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { FiAlertTriangle, FiBox, FiPlus, FiRefreshCw, FiUpload, FiUserCheck, FiArchive, FiPaperclip, FiClock } from 'react-icons/fi';
import { itAssetsAPI } from '../utils/api';
import { auditLogAPI } from '../utils/api';
import { getUser } from '../utils/auth';
import { CHART_COLORS } from '../utils/chartColors';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, PieChart, Pie, Cell, Legend } from 'recharts';
import indiaMap from '@svg-maps/india';

const OPEN_END_DATE = '9999-12-31';

const formatDate = (value) => {
  if (!value) return '-';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) return value.trim();
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const pad2 = (n) => String(n).padStart(2, '0');
  const yyyy = date.getFullYear();
  const mm = pad2(date.getMonth() + 1);
  const dd = pad2(date.getDate());
  const hasTime =
    (typeof value === 'string' && /[T:]/.test(value)) ||
    true; // non-date-only values should show time for auditability
  if (!hasTime) return `${yyyy}-${mm}-${dd}`;
  const hh = pad2(date.getHours());
  const mi = pad2(date.getMinutes());
  const ss = pad2(date.getSeconds());
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
};

const stringifyCell = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const inferMimeFromName = (name) => {
  const n = String(name || '').toLowerCase();
  if (n.endsWith('.pdf')) return 'application/pdf';
  if (n.endsWith('.png')) return 'image/png';
  if (n.endsWith('.jpg') || n.endsWith('.jpeg')) return 'image/jpeg';
  return '';
};

const ensureBlobType = (blob, mimeType) => {
  if (!blob) return blob;
  const desired = String(mimeType || '').trim();
  if (!desired) return blob;
  if (blob.type && blob.type !== 'application/octet-stream') return blob;
  try {
    return blob.slice(0, blob.size, desired);
  } catch {
    return blob;
  }
};

const normalizeStatusValue = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();

  // Handle truncated/abbreviated variants coming from Excel/imports.
  // Examples seen: "In-", "In -", "In-Store", etc.
  if (lower === 'in-' || lower.startsWith('in-') || lower.startsWith('in -') || lower.startsWith('instore') || lower.startsWith('in store')) {
    return 'In Store';
  }

  if (lower === 'allocated') return 'Allocated';
  // Standardize legacy/free-text statuses to the allowed set.
  if (lower === 'inactive' || lower === 'in active' || lower === 'in repair' || lower === 'in stock' || lower === 'in- stock') return 'In Store';

  // Fallback: title-case-ish for readability while keeping original semantics.
  return raw;
};

const ALL_COLUMNS = [
  { key: 'serial_number', label: 'Serial Number' },
  { key: 'genie_asset_code', label: 'Genie Asset Code' },
  { key: 'manufacture', label: 'Manufacture(Make)' },
  { key: 'model_details', label: 'Model Details' },
  { key: 'location', label: 'Location' },
  { key: 'status', label: 'Status' },
  { key: 'asset_type', label: 'Asset Type' },
  { key: 'host_name', label: '(Host-Name)' },
  { key: 'alloc_emp_id', label: 'Emp ID' },
  { key: 'alloc_user_name', label: 'User Name' },
  { key: 'alloc_email', label: 'E-mail ID' },
  { key: 'ip_address', label: 'IP Address' },
  { key: 'os_details', label: 'OS Details' },
  { key: 'po_details', label: 'PO Details' },
  { key: 'sap_asset_code', label: 'SAP Asset Code' },
  { key: 'purchase_details', label: 'Purchase Details' },
  { key: 'invoice_date', label: 'Invoice Date' },
  { key: 'warranty_type', label: 'Warranty Type' },
  { key: 'warranty_start_date', label: 'Warranty Start DT' },
  { key: 'warranty_end_date', label: 'Warranty End DT' },
  { key: 'ram_size_gb', label: 'RAM Size(GB)' },
  { key: 'cpu', label: 'CPU' },
  { key: 'remark', label: 'Remark' },
  { key: 'asset_condition', label: 'Asset Condition' },
  { key: 'condition_issues', label: 'Condition Issues' },
  // allocation (open row)
  { key: 'alloc_start_date', label: 'Allocated Date' },
  { key: 'alloc_end_date', label: 'Allocated End Date' },
  { key: 'alloc_request_id', label: 'SR Number' },
  { key: 'alloc_remark', label: 'Allocation Remark' },
	  // removed: alloc_last_deallocation_date / alloc_deactivation_date
	  // removed: alloc_reason_for_deactivation / alloc_deactivation_remarks (not needed)
	];

// Asset Master should focus on inventory/master attributes; host name is managed during allocation.
const MASTER_COLUMNS = ALL_COLUMNS.filter(
  (c) => !c.key.startsWith('alloc_') && c.key !== 'host_name' && c.key !== 'os_flavour' && c.key !== 'os_flavor'
);

// Active Asset (Current Allocation) tab shows everything except Asset Condition —
// that stays Overall Asset Master-only for display (it's still editable from here
// via the "Set Condition" row action, just not shown as a column in this table).
const ACTIVE_ASSET_COLUMNS = ALL_COLUMNS.filter(
  (c) => c.key !== 'asset_condition' && c.key !== 'condition_issues'
);

const HISTORY_COLUMNS = [
  { key: 'event_timestamp', label: 'Event Timestamp' },
  { key: 'event_date', label: 'Event Date' },
  { key: 'event_type', label: 'Event Type' },
  ...ALL_COLUMNS.map((c) => (c.key === 'status' ? { key: 'allocation_status', label: 'Status' } : c)),
];

const USER_HISTORY_COLUMNS = [
  { key: 'event_timestamp', label: 'Event Timestamp' },
  { key: 'serial_number', label: 'Serial Number' },
  { key: 'genie_asset_code', label: 'Genie Asset Code' },
  { key: 'manufacture', label: 'Manufacture(Make)' },
  { key: 'model_details', label: 'Model Details' },
  { key: 'location', label: 'Location' },
  { key: 'asset_type', label: 'Asset Type' },
  { key: 'alloc_emp_id', label: 'Emp ID' },
  { key: 'alloc_user_name', label: 'User Name' },
  { key: 'alloc_email', label: 'E-mail ID' },
  { key: 'alloc_start_date', label: 'Allocated Date' },
  { key: 'alloc_end_date', label: 'Allocated End Date' },
  { key: 'alloc_request_id', label: 'SR Number' },
  { key: 'alloc_remark', label: 'Allocation Remark' },
  { key: 'deactivation_reason', label: 'Deactivation Reason' },
];

const MY_ASSETS_TABLE_COLUMNS = [
  { key: 'serial_number', label: 'Serial Number' },
  { key: 'genie_asset_code', label: 'Genie Asset Code' },
  { key: 'manufacture', label: 'Manufacture(Make)' },
  { key: 'model_details', label: 'Model Details' },
  { key: 'location', label: 'Location' },
  { key: 'asset_type', label: 'Asset Type' },
  { key: 'host_name', label: '(Host-Name)' },
  { key: 'alloc_emp_id', label: 'Emp ID' },
  { key: 'alloc_user_name', label: 'User Name' },
  { key: 'alloc_email', label: 'E-mail ID' },
  { key: 'ip_address', label: 'IP Address' },
  { key: 'os_details', label: 'OS Details' },
  { key: 'warranty_start_date', label: 'Warranty Start DT' },
  { key: 'warranty_end_date', label: 'Warranty End DT' },
  { key: 'ram_size_gb', label: 'RAM Size(GB)' },
  { key: 'cpu', label: 'CPU' },
  { key: 'alloc_start_date', label: 'Allocated Date' }
];

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const computeAgeYears = (row) => {
  const start =
    parseDate(row?.warranty_start_date) ||
    parseDate(row?.warty_start_dt) ||
    parseDate(row?.warranty_end_date);
  if (!start) return null;
  const now = new Date();
  const diffMs = now.getTime() - start.getTime();
  const years = diffMs / (365.25 * 24 * 60 * 60 * 1000);
  return years >= 0 ? years : null;
};

const computeAgeMeta = (row) => {
  const ageYears = computeAgeYears(row);
  if (ageYears == null) return { ageYears: null, label: '-', variant: 'secondary', isOld: false };
  const isOld = ageYears > 3;
  return {
    ageYears,
    label: `${ageYears.toFixed(1)}y`,
    variant: isOld ? 'danger' : 'success',
    isOld
  };
};

const normalizeDropdownValue = (value) => (
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
);

const uniqueStatusOptions = (rows) => {
  const map = new Map();
  for (const row of rows || []) {
    const canonical = normalizeStatusValue(row?.status);
    if (!canonical) continue;
    const normalized = normalizeDropdownValue(canonical);
    if (!normalized) continue;
    if (!map.has(normalized)) map.set(normalized, canonical);
  }
  return Array.from(map.values()).sort((a, b) => a.localeCompare(b));
};

const uniqueValuesByNormalized = (rows, key) => {
  const canonicalByNormalized = new Map();
  for (const row of rows || []) {
    const raw = row?.[key];
    const canonical = stringifyCell(raw);
    if (!canonical || canonical === '-') continue;
    const normalized = normalizeDropdownValue(canonical);
    if (!normalized) continue;
    if (!canonicalByNormalized.has(normalized)) {
      canonicalByNormalized.set(normalized, canonical.trim());
    }
  }
  return Array.from(canonicalByNormalized.values()).sort((a, b) => a.localeCompare(b));
};

// Maps IT Asset `location` values to the exact state names used by
// @svg-maps/india. This list is maintained by hand against whatever values
// actually show up in real data — the admin Location lookup table can grow
// over time, so anything not listed here still shows up (as an "unmapped"
// badge below the map, see UNMAPPED_LOCATION_HINTS) rather than silently
// vanishing, making gaps like this visible instead of hidden.
const LOCATION_TO_INDIA_STATE = {
  bangalore: 'Karnataka',
  bengaluru: 'Karnataka',
  bhopal: 'Madhya Pradesh',
  chennai: 'Tamil Nadu',
  gurgaon: 'Haryana',
  gurugram: 'Haryana',
  guwahati: 'Assam',
  hyderabad: 'Telangana',
  jaipur: 'Rajasthan',
  'jammu and srinagar': 'Jammu and Kashmir',
  kolkata: 'West Bengal',
  nagpur: 'Maharashtra',
  odisha: 'Odisha',
  mumbai: 'Maharashtra',
  pune: 'Maharashtra',
  waluj: 'Maharashtra',
  'shendra glass plant': 'Maharashtra',
  shendra: 'Maharashtra',
  aurangabad: 'Maharashtra',
  ahmedabad: 'Gujarat',
  rakholi: 'Dadra and Nagar Haveli',
  silvassa: 'Dadra and Nagar Haveli',
  delhi: 'Delhi',
  noida: 'Uttar Pradesh',
  lucknow: 'Uttar Pradesh',
  chandigarh: 'Chandigarh',
  indore: 'Madhya Pradesh',
  ludhiana: 'Punjab',
  patna: 'Bihar',
  ranchi: 'Jharkhand',
  bhubaneswar: 'Odisha',
  goa: 'Goa',
  kochi: 'Kerala',
  cochin: 'Kerala',
  coimbatore: 'Tamil Nadu',
  vadodara: 'Gujarat',
  surat: 'Gujarat'
};

// Location values that describe cloud-hosted/virtual infrastructure, not a
// physical place — these must never be shown as "outside India", since
// that phrasing implies a real location. Matched by substring so variants
// like "GCP Mumbai Region" or "AWS Cloud" are also caught.
const VIRTUAL_LOCATION_KEYWORDS = ['gcp', 'azure', 'aws', 'cloud', 'google cloud', 'amazon web services'];
const isVirtualLocation = (label) => {
  const text = normalizeDropdownValue(label);
  return VIRTUAL_LOCATION_KEYWORDS.some((kw) => text.includes(kw));
};

const buildStateAssetTotals = (attachmentsByLocation) => {
  const byState = new Map();
  const outsideMap = new Map();
  const virtualMap = new Map();
  for (const row of attachmentsByLocation || []) {
    const key = normalizeDropdownValue(row?.location);
    const stateName = LOCATION_TO_INDIA_STATE[key];
    const total = Number(row?.totalAssets || 0) || 0;
    if (stateName) {
      const cur = byState.get(stateName) || { total: 0, allocated: 0, inStore: 0, withAttachments: 0 };
      cur.total += total;
      cur.allocated += Number(row?.allocatedAssets || 0) || 0;
      cur.inStore += Number(row?.inStoreAssets || 0) || 0;
      cur.withAttachments += Number(row?.totalWithAttachments || 0) || 0;
      byState.set(stateName, cur);
    } else {
      const label = row?.location || 'Unknown';
      if (isVirtualLocation(label)) {
        virtualMap.set(label, (virtualMap.get(label) || 0) + total);
      } else {
        if (key && key !== 'uk') {
          // eslint-disable-next-line no-console
          console.warn(`[IT Assets Map] Location "${label}" is not mapped to an Indian state; showing it under "outside India". Add it to LOCATION_TO_INDIA_STATE if it's an Indian location.`);
        }
        outsideMap.set(label, (outsideMap.get(label) || 0) + total);
      }
    }
  }
  return { byState, outsideMap, virtualMap };
};

const MAP_COLOR_EMPTY = '#e9ecef';
const MAP_COLOR_LOW = '#dde2fb';
const MAP_COLOR_HIGH = '#4c3480';

const hexToRgb = (hex) => {
  const n = parseInt(hex.replace('#', ''), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

const lerpColor = (hexA, hexB, t) => {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  const r = Math.round(a.r + (b.r - a.r) * t);
  const g = Math.round(a.g + (b.g - a.g) * t);
  const bl = Math.round(a.b + (b.b - a.b) * t);
  return `rgb(${r}, ${g}, ${bl})`;
};

// sqrt domain (not linear): with a handful of large offices and many small
// ones, a linear count/maxCount ramp compresses most states into
// near-identical light tints — sqrt keeps small states visually distinct.
const colorForStateCount = (count, maxCount) => {
  if (!count || maxCount <= 0) return MAP_COLOR_EMPTY;
  const t = Math.min(1, Math.sqrt(count) / Math.sqrt(maxCount));
  return lerpColor(MAP_COLOR_LOW, MAP_COLOR_HIGH, t);
};

const IndiaAssetChoropleth = ({ attachmentsByLocation, height = 360 }) => {
  const wrapRef = useRef(null);
  const [hover, setHover] = useState(null);

  const { byState, outsideMap, virtualMap } = useMemo(() => buildStateAssetTotals(attachmentsByLocation), [attachmentsByLocation]);
  const maxCount = useMemo(() => Math.max(0, ...Array.from(byState.values()).map((v) => v.total)), [byState]);

  const handleMove = (e, loc, stat) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    setHover({
      name: loc.name,
      stat: stat || { total: 0, allocated: 0, inStore: 0 },
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <svg viewBox={indiaMap.viewBox} style={{ width: '100%', height }} role="img" aria-label="Assets by Indian state">
        {indiaMap.locations.map((loc) => {
          const stat = byState.get(loc.name);
          return (
            <path
              key={loc.id}
              d={loc.path}
              fill={colorForStateCount(stat?.total, maxCount)}
              stroke="#ffffff"
              strokeWidth={0.75}
              onMouseMove={(e) => handleMove(e, loc, stat)}
              onMouseLeave={() => setHover(null)}
              style={{ cursor: 'default' }}
            />
          );
        })}
      </svg>
      {hover && (
        <div
          className="shadow-sm bg-white rounded p-2 small"
          style={{ position: 'absolute', left: hover.x + 12, top: hover.y - 12, pointerEvents: 'none', minWidth: 140, zIndex: 5, border: '1px solid #e5e7eb' }}
        >
          <div className="fw-semibold">{hover.name}</div>
          <div className="text-muted">{hover.stat.total} asset(s)</div>
          {hover.stat.total > 0 && (
            <div className="text-muted">{hover.stat.allocated} allocated · {hover.stat.inStore} in store</div>
          )}
        </div>
      )}
      {(outsideMap.size > 0 || virtualMap.size > 0) && (
        <div className="small mt-2 d-flex flex-wrap gap-2">
          {Array.from(outsideMap.entries()).map(([label, total]) => (
            <Badge key={label} bg="secondary">+{total} outside India ({label})</Badge>
          ))}
          {Array.from(virtualMap.entries()).map(([label, total]) => (
            <Badge key={label} bg="info" text="dark">+{total} cloud-hosted ({label})</Badge>
          ))}
        </div>
      )}
    </div>
  );
};

IndiaAssetChoropleth.propTypes = {
  attachmentsByLocation: PropTypes.arrayOf(PropTypes.shape({
    location: PropTypes.string,
    totalAssets: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    allocatedAssets: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    inStoreAssets: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    totalWithAttachments: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  })),
  height: PropTypes.number
};

IndiaAssetChoropleth.defaultProps = {
  attachmentsByLocation: [],
  height: 360
};

const LocationLeaderboard = ({ attachmentsByLocation }) => {
  const rows = useMemo(() => {
    const sorted = [...(attachmentsByLocation || [])].sort((a, b) => (Number(b?.totalAssets || 0) - Number(a?.totalAssets || 0)));
    return sorted.filter((r) => Number(r?.totalAssets || 0) > 0);
  }, [attachmentsByLocation]);
  const maxCount = useMemo(() => Math.max(0, ...rows.map((r) => Number(r?.totalAssets || 0))), [rows]);
  const top = rows.slice(0, 8);
  const restCount = rows.length - top.length;

  if (!rows.length) return <p className="text-muted mb-0">No location data available.</p>;

  return (
    <div>
      {top.map((row) => {
        const total = Number(row?.totalAssets || 0);
        const pct = maxCount > 0 ? Math.round((total / maxCount) * 100) : 0;
        return (
          <div key={row.location} className="mb-2">
            <div className="d-flex justify-content-between small mb-1">
              <span>{row.location}</span>
              <span className="fw-semibold">{total}</span>
            </div>
            <div style={{ height: 6, background: '#eef1f6', borderRadius: 999 }}>
              <div style={{ height: '100%', width: `${pct}%`, borderRadius: 999, background: colorForStateCount(total, maxCount) }} />
            </div>
          </div>
        );
      })}
      {restCount > 0 && <div className="text-muted small mt-1">+{restCount} more location(s)</div>}
    </div>
  );
};

LocationLeaderboard.propTypes = {
  attachmentsByLocation: PropTypes.arrayOf(PropTypes.shape({
    location: PropTypes.string,
    totalAssets: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }))
};

LocationLeaderboard.defaultProps = {
  attachmentsByLocation: []
};

const DONUT_MAX_SLICES = 6;

// Groups anything beyond the top N into a single "Other" slice — a donut
// with 12+ thin slices and a legend that wraps three lines is illegible;
// top-N + Other is the standard BI-dashboard treatment for a long-tail
// category breakdown like asset_type tends to be.
const buildDonutSlices = (data) => {
  const sorted = [...data].sort((a, b) => b.value - a.value);
  if (sorted.length <= DONUT_MAX_SLICES) return sorted;
  const top = sorted.slice(0, DONUT_MAX_SLICES - 1);
  const otherValue = sorted.slice(DONUT_MAX_SLICES - 1).reduce((sum, d) => sum + d.value, 0);
  return [...top, { name: 'Other', value: otherValue }];
};

const AssetTypeDonut = ({ data, total }) => {
  if (!data.length) return <p className="text-muted mb-0">No asset type data available.</p>;
  const slices = buildDonutSlices(data);
  return (
    <div style={{ width: '100%', height: 320, position: 'relative' }}>
      <ResponsiveContainer>
        <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
          <Pie
            data={slices}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="42%"
            innerRadius={62}
            outerRadius={98}
            paddingAngle={2}
            cornerRadius={4}
            stroke="#ffffff"
            strokeWidth={2}
          >
            {slices.map((entry, index) => (
              <Cell key={entry.name} fill={entry.name === 'Other' ? '#cbd5e1' : CHART_COLORS[index % CHART_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(value, name) => [`${value} asset(s)`, name]} />
          <Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
        </PieChart>
      </ResponsiveContainer>
      <div style={{ position: 'absolute', top: '39%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
        <div className="fw-bold fs-4">{total}</div>
        <div className="text-muted small">Total</div>
      </div>
    </div>
  );
};

AssetTypeDonut.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, value: PropTypes.number })).isRequired,
  total: PropTypes.number.isRequired
};

const TopFaultyComponents = ({ data }) => {
  if (!data.length) return <p className="text-muted mb-0">No faulty-component data — no assets are currently marked Not Working with recorded issues.</p>;
  const maxCount = Math.max(0, ...data.map((d) => d.count));
  return (
    <div>
      {data.map((d) => {
        const pct = maxCount > 0 ? Math.round((d.count / maxCount) * 100) : 0;
        return (
          <div key={d.name} className="mb-2">
            <div className="d-flex justify-content-between small mb-1">
              <span>{d.name}</span>
              <span className="fw-semibold">{d.count}</span>
            </div>
            <div style={{ height: 6, background: '#eef1f6', borderRadius: 999 }}>
              <div style={{ height: '100%', width: `${pct}%`, borderRadius: 999, background: 'linear-gradient(90deg, #fb7185, #e11d48)' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

TopFaultyComponents.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, count: PropTypes.number })).isRequired
};

const buildSearchSuggestions = ({ rows, keys, limit = 30 }) => {
  const normalizedToValue = new Map();
  const safeKeys = (keys || []).filter(Boolean);
  for (const row of (rows || [])) {
    for (const key of safeKeys) {
      const raw = row?.[key];
      const text = stringifyCell(raw);
      if (!text || text === '-') continue;
      const normalized = normalizeDropdownValue(text);
      if (!normalized) continue;
      if (!normalizedToValue.has(normalized)) normalizedToValue.set(normalized, text.trim());
      if (normalizedToValue.size >= limit) break;
    }
    if (normalizedToValue.size >= limit) break;
  }
  return Array.from(normalizedToValue.values()).sort((a, b) => a.localeCompare(b));
};

const buildLookupCanonicalMap = (values) => {
  const map = new Map();
  for (const v of Array.isArray(values) ? values : []) {
    const normalized = normalizeDropdownValue(v);
    if (!normalized) continue;
    if (!map.has(normalized)) map.set(normalized, v);
  }
  return map;
};

const toTitleCase = (value) => (
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase())
);

const StatCard = ({ icon, label, value, subtitle, tone = 'primary', onClick, active = false }) => (
  <Card
    className="border-0 shadow-sm h-100 it-assets-card"
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    style={{
      cursor: onClick ? 'pointer' : undefined,
      outline: active ? '2px solid var(--bs-danger, #dc3545)' : undefined
    }}
  >
    <Card.Body className="d-flex align-items-center gap-3">
      <div className={`it-assets-stat-icon it-assets-stat-icon--${tone}`}>
        {icon}
      </div>
      <div className="flex-grow-1">
        <div className="text-muted small">{label}</div>
        <div className="fw-semibold fs-5">{value}</div>
        {subtitle && <div className="text-muted" style={{ fontSize: 11 }}>{subtitle}</div>}
      </div>
    </Card.Body>
  </Card>
);

const DataTable = ({
  rows = [],
  columns = [],
  lookups = {},
  canManage,
  canDelete = false,
  canEditMaster = false,
  onAssetIdClick,
  onUserClick,
  onAllocate,
  onDeallocate,
  onDeactivate,
  onLocationChange,
  onSetCondition,
  onDelete,
  onEditMaster,
  onOpenAttachments,
  showAttachmentColumn = false,
  showAllocationFilter = false,
  showBlankStatusForDeactivated = false,
  exportName = 'it-assets'
}) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [attachmentFilter, setAttachmentFilter] = useState(''); // with | without

  const datalistSuggestId = useMemo(() => `it-assets-suggest-${String(exportName || 'it-assets').replace(/[^a-z0-9_-]/gi, '-')}`, [exportName]);
  const searchSuggestionKeys = useMemo(() => {
    const preferred = [
      'serial_number',
      'asset_id',
      'genie_asset_code',
      'location',
      'asset_type',
      'manufacture',
      'model_details',
      'sap_asset_code',
      'po_details',
      'alloc_email',
      'alloc_user_name',
      'alloc_emp_id'
    ];
    const available = new Set((columns || []).map((c) => c.key));
    return preferred.filter((k) => available.has(k));
  }, [columns]);

  const searchSuggestions = useMemo(
    () => buildSearchSuggestions({ rows, keys: searchSuggestionKeys, limit: 40 }),
    [rows, searchSuggestionKeys]
  );

  const downloadXlsx = (xlsxRows, filenameBase) => {
    try {
      auditLogAPI.logClientEvent({
        module: 'it_assets',
        action: 'download_xlsx',
        description: `Downloaded ${exportName} (xlsx)`,
        changedData: {
          exportName,
          filters: {
            search: String(search || '').trim() || null,
            status: String(statusFilter || '').trim() || null,
            type: String(typeFilter || '').trim() || null,
            location: String(locationFilter || '').trim() || null,
            attachment: showAttachmentColumn ? (String(attachmentFilter || '').trim() || null) : null
          },
          count: (xlsxRows || []).length
        }
      });
    } catch {
      // ignore
    }

    const headers = columns.map((c) => c.label);
    const aoa = [
      headers,
      ...(xlsxRows || []).map((r) => (
        columns.map((c) => {
          const raw = r?.[c.key];
          if (c.key.endsWith('_date') || c.key.includes('_dt')) return formatDate(raw);
          return stringifyCell(raw);
        })
      ))
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filenameBase}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const filtered = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    const statusKey = String(statusFilter || '').trim().toLowerCase();
	    const typeKey = String(typeFilter || '').trim().toLowerCase();
	    const locationKey = String(locationFilter || '').trim().toLowerCase();

	    return (rows || []).filter((r) => {
	      if (statusKey && normalizeDropdownValue(normalizeStatusValue(r?.status)) !== statusKey) return false;
	      if (typeKey && normalizeDropdownValue(r?.asset_type) !== typeKey) return false;
	      if (locationKey && normalizeDropdownValue(r?.location) !== locationKey) return false;
	      if (showAttachmentColumn) {
	        const cnt = Number(r?.attachment_count || 0) || 0;
	        if (attachmentFilter === 'with' && cnt <= 0) return false;
	        if (attachmentFilter === 'without' && cnt > 0) return false;
      }
      if (!q) return true;

      const hay = columns
        .map((c) => r?.[c.key])
        .filter((v) => v !== null && v !== undefined)
        .map((v) => stringifyCell(v))
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
	  }, [attachmentFilter, columns, locationFilter, rows, search, showAttachmentColumn, statusFilter, typeFilter]);

  const statusOptions = useMemo(
    () => {
      const source = (Array.isArray(lookups?.status) && lookups.status.length) ? lookups.status : uniqueStatusOptions(rows);
      const map = new Map();
      for (const v of source || []) {
        const canonical = normalizeStatusValue(v);
        if (!canonical) continue;
        const normalized = normalizeDropdownValue(canonical);
        if (!normalized) continue;
        if (!map.has(normalized)) map.set(normalized, canonical);
      }
      return Array.from(map.values()).sort((a, b) => a.localeCompare(b));
    },
    [lookups, rows]
  );
  const typeOptions = useMemo(
    () => (Array.isArray(lookups?.asset_type) && lookups.asset_type.length ? lookups.asset_type : uniqueValuesByNormalized(rows, 'asset_type')),
    [lookups, rows]
  );
  const locationOptions = useMemo(
    () => (Array.isArray(lookups?.location) && lookups.location.length ? lookups.location : uniqueValuesByNormalized(rows, 'location')),
    [lookups, rows]
  );

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <Row className="align-items-end g-2 mb-3">
            <Col md={4}>
              <Form.Label>Search</Form.Label>
              <Form.Control
                value={search}
                placeholder="Serial / code / user / location..."
                onChange={(e) => setSearch(e.target.value)}
                list={datalistSuggestId}
              />
              <datalist id={datalistSuggestId}>
                {searchSuggestions.map((v) => (
                  <option key={v} value={v} />
                ))}
              </datalist>
            </Col>
            <Col md={2}>
              <Form.Label>Status</Form.Label>
              <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All</option>
                {statusOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label>Type</Form.Label>
              <Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="">All</option>
                {typeOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label>Location</Form.Label>
              <Form.Select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
                <option value="">All</option>
                {locationOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </Form.Select>
            </Col>
          {showAttachmentColumn && (
            <Col md={2}>
              <Form.Label>Attachment</Form.Label>
              <Form.Select value={attachmentFilter} onChange={(e) => setAttachmentFilter(e.target.value)}>
                <option value="">All</option>
                <option value="with">Uploaded</option>
                <option value="without">Not Uploaded</option>
              </Form.Select>
            </Col>
          )}
            <Col md={12} className="text-md-end">
              <div className="d-flex justify-content-between justify-content-md-end align-items-center gap-2">
                <div className="text-muted small">Total: {filtered.length}</div>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => {
                    const hasFilters = Boolean(
                      String(search || '').trim() ||
                      String(statusFilter || '').trim() ||
                      String(typeFilter || '').trim() ||
                      String(locationFilter || '').trim() ||
                      (showAttachmentColumn && String(attachmentFilter || '').trim())
                    );
                    const toDownload = hasFilters ? filtered : rows;
                    const suffix = hasFilters ? 'filtered' : 'all';
                    downloadXlsx(toDownload, `${exportName}-${suffix}`);
                  }}
                  disabled={!(rows || []).length}
                >
                  Download
                </Button>
              </div>
            </Col>
        </Row>

        <div className="table-responsive" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh' }}>
          <Table hover size="sm" className="align-middle mb-0 small">
            <thead className="table-light">
              <tr>
                {columns.map((c) => {
                  if (canManage && c.key === 'status') {
                    return (
                      <React.Fragment key="status-and-actions-head">
                        <th className="text-nowrap">{c.label}</th>
                        <th className="text-nowrap">Action</th>
                        {showAttachmentColumn && <th className="text-nowrap">Attachment</th>}
                      </React.Fragment>
                    );
                  }
                  return <th key={c.key} className="text-nowrap">{c.label}</th>;
                })}
                {((canEditMaster && onEditMaster) || (canDelete && onDelete)) ? <th className="text-nowrap">Action</th> : null}
              </tr>
            </thead>
            <tbody>
	              {filtered.map((r) => (
	                <tr key={r.serial_number}>
	                  {columns.map((c) => {
	                    const raw = r?.[c.key];
	                    const value = c.key.endsWith('_date') || c.key.includes('_dt') ? formatDate(raw) : stringifyCell(raw);

		                    if (c.key === 'serial_number' && onAssetIdClick && r?.serial_number) {
		                      return (
		                        <td key={c.key} className="text-nowrap">
		                          <Button
		                            variant="link"
		                            size="sm"
		                            className="p-0"
		                            onClick={() => onAssetIdClick(String(r.serial_number))}
		                            title="Open asset history"
		                          >
		                            {value}
		                          </Button>
		                        </td>
		                      );
		                    }

                        if (c.key === 'alloc_email' && typeof onUserClick === 'function' && String(raw || '').trim()) {
                          return (
                            <td key={c.key} className="text-nowrap">
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0"
                                onClick={() => onUserClick(String(raw))}
                                title="Open user history"
                              >
                                {value}
                              </Button>
                            </td>
                          );
                        }

                    if (c.key === 'status') {
                      if (canManage) {
                        const statusLabel = raw ? normalizeStatusValue(raw) : '-';
                        return (
                          <React.Fragment key="status-and-actions">
                            <td className="text-nowrap">
                              <Badge bg="secondary">{statusLabel}</Badge>
                            </td>
                            <td className="text-nowrap">
                              <div className="d-flex gap-2">
                                <Form.Select
                                  size="sm"
                                  style={{ width: 160 }}
                                  defaultValue=""
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    e.target.value = '';
                                    if (v === 'allocate') return onAllocate?.(r);
                                    if (v === 'deallocate') return onDeallocate?.(r);
                                    if (v === 'deactivate') return onDeactivate?.(r);
                                    if (v === 'location_change') return onLocationChange?.(r);
                                    if (v === 'set_condition') return onSetCondition?.(r);
                                  }}
                                >
                                  <option value="">Select Action</option>
                                  <option value="allocate">Allocate</option>
                                  <option value="deallocate">Deallocate</option>
                                  <option value="location_change">Change Location</option>
                                  <option value="deactivate">Deactivate</option>
                                </Form.Select>
                                {canDelete && (
                                  <Button size="sm" variant="outline-dark" onClick={() => onDelete?.(r)}>
                                    Delete
                                  </Button>
                                )}
                              </div>
                            </td>
                            {showAttachmentColumn ? (
                              <td className="text-nowrap">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => onOpenAttachments?.(r)}
                                  disabled={!r?.serial_number}
                                >
                                  Attachment ({Number(r?.attachment_count || 0) || 0})
                                </Button>
                              </td>
                            ) : null}
                          </React.Fragment>
                        );
                      }

                      const statusLabel = raw ? normalizeStatusValue(raw) : '';
                      const isDeactivated = showBlankStatusForDeactivated && String(raw || '').trim().toLowerCase() === 'deactivated';
                      return (
                        <td key={c.key} className="text-nowrap">
                          {isDeactivated ? (
                            <Badge bg="dark" className="text-white">{statusLabel || 'Deactivated'}</Badge>
                          ) : (
                            <Badge bg="secondary">{statusLabel || '-'}</Badge>
                          )}
                        </td>
                      );
                    }

                    if (c.key === 'asset_condition') {
                      const isNotWorking = String(raw || '').trim().toLowerCase() === 'not working';
                      return (
                        <td key={c.key} className="text-nowrap">
                          <Badge bg={isNotWorking ? 'danger' : 'success'}>{raw ? String(raw) : 'Working'}</Badge>
                        </td>
                      );
                    }

                    return (
                      <td key={c.key} className="text-nowrap">{value}</td>
                    );
                  })}
                  {((canEditMaster && onEditMaster) || (canDelete && onDelete)) ? (
                    <td className="text-nowrap">
                      <div className="d-flex gap-2">
                        {canEditMaster && onEditMaster && (
                          <Button size="sm" variant="outline-primary" onClick={() => onEditMaster(r)}>
                            Edit
                          </Button>
                        )}
                        {canDelete && onDelete && (
                          <Button size="sm" variant="outline-dark" onClick={() => onDelete(r)}>
                            Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}

              {!filtered.length && (
                <tr>
                  <td colSpan={columns.length} className="text-center text-muted py-4">
                    No assets found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
};

const MyAssetsPanel = ({ rows = [], lookups = {}, onAssetIdClick }) => {
  const stats = useMemo(() => {
    const total = rows?.length || 0;
    const ages = (rows || []).map((r) => computeAgeYears(r)).filter((v) => typeof v === 'number' && Number.isFinite(v));
    const oldCount = ages.filter((v) => v > 3).length;
    const oldest = ages.length ? Math.max(...ages) : null;

    return {
      total,
      oldCount,
      oldestLabel: oldest == null ? '-' : `${oldest.toFixed(1)}y`
    };
  }, [rows]);

  const locationCanonical = useMemo(() => buildLookupCanonicalMap(lookups?.location), [lookups]);
  const typeCanonical = useMemo(() => buildLookupCanonicalMap(lookups?.asset_type), [lookups]);

  return (
    <>
      <Card className="it-assets-hero border-0 shadow-sm mb-3">
        <Card.Body>
          <Row className="g-3 align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center gap-2 mb-1">
                <div className="it-assets-hero-badge"><FiBox /></div>
                <div>
                  <div className="h4 mb-0">My Assets</div>
                  <div className="text-muted small">
                    Quick view of assets currently allocated to you.
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <StatCard icon={<FiBox />} label="Allocated Assets" value={stats.total} tone="primary" />
        </Col>
        <Col md={6}>
          <StatCard icon={<FiAlertTriangle />} label="Oldest Asset" value={stats.oldestLabel} tone="danger" />
        </Col>
      </Row>

      {!rows?.length ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="py-5">
            <div className="text-center">
              <div className="it-assets-empty-icon mb-2">
                <FiBox />
              </div>
              <div className="fw-semibold mb-1">No assets allocated to you</div>
              <div className="text-muted small">
                If you believe this is a mistake, contact the IT team.
              </div>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <div className="table-responsive">
              <Table hover bordered size="sm" className="align-middle mb-0 small">
                <thead className="table-light">
                  <tr>
                    <th className="text-nowrap">Age</th>
                    {MY_ASSETS_TABLE_COLUMNS.map((c) => (
                      <th key={c.key} className="text-nowrap">{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
	                  {(rows || []).map((r) => (
	                    <tr key={r.serial_number}>
                      <td className="text-nowrap">
                        {(() => {
                          const age = computeAgeMeta(r);
                          return <Badge bg={age.variant}>{age.label}</Badge>;
                        })()}
                      </td>
	                      {MY_ASSETS_TABLE_COLUMNS.map((c) => {
	                        const raw = r?.[c.key];
	                        let value = c.key.endsWith('_date') || c.key.includes('_dt') ? formatDate(raw) : stringifyCell(raw);

                        // Display canonical casing for common dropdown-backed fields.
                        if (c.key === 'location') {
                          const normalized = normalizeDropdownValue(value);
                          value = locationCanonical.get(normalized) || toTitleCase(value);
                        }
                        if (c.key === 'asset_type') {
                          const normalized = normalizeDropdownValue(value);
                          value = typeCanonical.get(normalized) || toTitleCase(value);
                        }

		                        if (c.key === 'serial_number' && typeof onAssetIdClick === 'function' && r?.serial_number) {
		                          return (
		                            <td key={c.key} className="text-nowrap">
		                              <Button
		                                variant="link"
		                                size="sm"
		                                className="p-0"
		                                onClick={() => onAssetIdClick(String(r.serial_number))}
		                                title="Open asset history"
		                              >
		                                {value}
		                              </Button>
		                            </td>
		                          );
		                        }
	                        return <td key={c.key} className="text-nowrap">{value}</td>;
	                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

const ITAssetDashboardPanel = ({ canManage }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusCounts, setStatusCounts] = useState([]);
  const [attachmentsByLocation, setAttachmentsByLocation] = useState([]);
  const [inventoryRows, setInventoryRows] = useState([]);

  const [statusFilter, setStatusFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [ageBucket, setAgeBucket] = useState(''); // '', lt3, 3to5, 5to7, gt7
  const [attachmentFilter, setAttachmentFilter] = useState(''); // '', with, without
  const [conditionFilter, setConditionFilter] = useState(''); // '', Working, Not Working

  const isAssetNotWorking = useCallback((row) => (
    String(row?.asset_condition || 'Working').trim().toLowerCase() === 'not working'
  ), []);

  const isCurrentlyAllocated = useCallback((row) => Boolean(
    row?.alloc_user_id || row?.alloc_email || row?.alloc_user_name || row?.alloc_emp_id
  ), []);

  const load = useCallback(async () => {
    if (!canManage) return;
    setLoading(true);
    setError('');
    try {
      const [dashRes, invRes] = await Promise.all([
        itAssetsAPI.getDashboard(),
        itAssetsAPI.getInventory()
      ]);
      const payload = dashRes?.data?.data || dashRes?.data || {};
      setStatusCounts(Array.isArray(payload.statusCounts) ? payload.statusCounts : []);
      setAttachmentsByLocation(Array.isArray(payload.attachmentsByLocation) ? payload.attachmentsByLocation : []);
      const inv = invRes?.data?.data || invRes?.data || [];
      setInventoryRows(Array.isArray(inv) ? inv : []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  useEffect(() => {
    load();
  }, [load]);

  const locationOptions = useMemo(() => uniqueValuesByNormalized(inventoryRows, 'location'), [inventoryRows]);
  const typeOptions = useMemo(() => uniqueValuesByNormalized(inventoryRows, 'asset_type'), [inventoryRows]);

  // All filters except Condition — used to compute the "Not Working" count so
  // it stays meaningful even when the Condition filter itself is active.
  const baseFilteredInventory = useMemo(() => {
    const statusKey = normalizeDropdownValue(statusFilter);
    const locKey = normalizeDropdownValue(locationFilter);
    const typeKey = normalizeDropdownValue(typeFilter);
    return (inventoryRows || []).filter((r) => {
      const statusCanonical = normalizeStatusValue(r?.status);
      if (statusKey && normalizeDropdownValue(statusCanonical) !== statusKey) return false;
      if (locKey && normalizeDropdownValue(r?.location) !== locKey) return false;
      if (typeKey && normalizeDropdownValue(r?.asset_type) !== typeKey) return false;

      const ageYears = computeAgeYears(r);
      if (ageBucket) {
        if (ageYears == null) return false;
        if (ageBucket === 'lt3' && !(ageYears < 3)) return false;
        if (ageBucket === '3to5' && !(ageYears >= 3 && ageYears < 5)) return false;
        if (ageBucket === '5to7' && !(ageYears >= 5 && ageYears < 7)) return false;
        if (ageBucket === 'gt7' && !(ageYears >= 7)) return false;
      }

      const cnt = Number(r?.attachment_count || 0) || 0;
      if (attachmentFilter === 'with' && cnt <= 0) return false;
      if (attachmentFilter === 'without' && cnt > 0) return false;
      return true;
    });
  }, [ageBucket, attachmentFilter, inventoryRows, locationFilter, statusFilter, typeFilter]);

  const notWorkingCount = useMemo(
    () => (baseFilteredInventory || []).filter(isAssetNotWorking).length,
    [baseFilteredInventory, isAssetNotWorking]
  );

  const filteredInventory = useMemo(() => {
    if (!conditionFilter) return baseFilteredInventory;
    return (baseFilteredInventory || []).filter((r) => (
      conditionFilter === 'Not Working' ? isAssetNotWorking(r) : !isAssetNotWorking(r)
    ));
  }, [baseFilteredInventory, conditionFilter, isAssetNotWorking]);

  const ageStats = useMemo(() => {
    const rows = filteredInventory || [];
    let total = 0;
    let allocated = 0;
    let inStore = 0;
    let withAttachments = 0;
    let count3to5 = 0;
    let count5to7 = 0;
    let count7plus = 0;
    const ages = [];

    for (const r of rows) {
      total += 1;
      const statusCanonical = normalizeStatusValue(r?.status);
      const key = normalizeDropdownValue(statusCanonical);
      if (key === 'allocated') allocated += 1;
      if (key === 'in store') inStore += 1;
      const cnt = Number(r?.attachment_count || 0) || 0;
      if (cnt > 0) withAttachments += 1;
      const ageYears = computeAgeYears(r);
      if (typeof ageYears === 'number' && Number.isFinite(ageYears)) {
        ages.push(ageYears);
        if (ageYears >= 3 && ageYears < 5) count3to5 += 1;
        if (ageYears >= 5 && ageYears < 7) count5to7 += 1;
        if (ageYears >= 7) count7plus += 1;
      }
    }

    const avgAge = ages.length ? (ages.reduce((a, b) => a + b, 0) / ages.length) : null;
    return { total, allocated, inStore, withAttachments, count3to5, count5to7, count7plus, avgAge };
  }, [filteredInventory]);

  const ageBucketChartData = useMemo(() => {
    const out = [
      { bucket: '0–3y', allocated: 0, inStore: 0 },
      { bucket: '3–5y', allocated: 0, inStore: 0 },
      { bucket: '5–7y', allocated: 0, inStore: 0 },
      { bucket: '7y+', allocated: 0, inStore: 0 },
    ];
    for (const r of filteredInventory || []) {
      const ageYears = computeAgeYears(r);
      if (ageYears == null) continue;
      const idx = ageYears < 3 ? 0 : (ageYears < 5 ? 1 : (ageYears < 7 ? 2 : 3));
      const statusCanonical = normalizeStatusValue(r?.status);
      const key = normalizeDropdownValue(statusCanonical);
      if (key === 'allocated') out[idx].allocated += 1;
      else out[idx].inStore += 1;
    }
    return out;
  }, [filteredInventory]);

  const assetTypeBreakdown = useMemo(() => {
    const counts = new Map();
    for (const r of filteredInventory || []) {
      const type = stringifyCell(r?.asset_type);
      if (!type || type === '-') continue;
      counts.set(type, (counts.get(type) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredInventory]);

  const topFaultyComponents = useMemo(() => {
    const counts = new Map();
    for (const r of filteredInventory || []) {
      if (!isAssetNotWorking(r)) continue;
      const issues = stringifyCell(r?.condition_issues);
      if (!issues || issues === '-') continue;
      for (const raw of issues.split(',')) {
        const name = raw.trim();
        if (!name) continue;
        counts.set(name, (counts.get(name) || 0) + 1);
      }
    }
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [filteredInventory, isAssetNotWorking]);

  const oldestAssetsAllocated = useMemo(() => {
    return (filteredInventory || [])
      .filter((r) => isCurrentlyAllocated(r))
      .map((r) => ({ row: r, ageYears: computeAgeYears(r) }))
      .filter((x) => x.ageYears != null)
      .sort((a, b) => b.ageYears - a.ageYears)
      .slice(0, 15);
  }, [filteredInventory, isCurrentlyAllocated]);

  const oldestUsersAllocated = useMemo(() => {
    const map = new Map();
    for (const r of filteredInventory || []) {
      if (!isCurrentlyAllocated(r)) continue;
      const key = String(r?.alloc_email || r?.alloc_emp_id || '').trim();
      if (!key) continue;
      const ageYears = computeAgeYears(r);
      if (ageYears == null) continue;
      const cur = map.get(key) || { userKey: key, oldestAge: 0, oldestSerial: '', count: 0, count5plus: 0 };
      cur.count += 1;
      if (ageYears >= 5) cur.count5plus += 1;
      if (ageYears > (cur.oldestAge || 0)) {
        cur.oldestAge = ageYears;
        cur.oldestSerial = String(r?.serial_number || '');
      }
      map.set(key, cur);
    }
    return Array.from(map.values())
      .sort((a, b) => (b.oldestAge - a.oldestAge) || (b.count5plus - a.count5plus) || (b.count - a.count))
      .slice(0, 15);
  }, [filteredInventory, isCurrentlyAllocated]);

  const dashboardDetailColumns = useMemo(() => [
    { key: 'serial_number', label: 'Serial Number' },
    { key: 'genie_asset_code', label: 'Genie Asset Code' },
    { key: 'asset_type', label: 'Asset Type' },
    { key: 'model_details', label: 'Model Details' },
    { key: 'location', label: 'Location' },
    { key: 'status', label: 'Status' },
    { key: 'asset_condition', label: 'Asset Condition' },
    { key: 'condition_issues', label: 'Condition Issues' },
    { key: 'alloc_start_date', label: 'Allocated Since' },
    { key: 'alloc_end_date', label: 'Allocated Until' },
    { key: 'alloc_user_name', label: 'User Name' },
    { key: 'alloc_email', label: 'User Email' },
    { key: 'attachment_count', label: 'Attachments' }
  ], []);

  const downloadDashboardInventory = useCallback(() => {
    const rowsToExport = filteredInventory || [];
    const headers = dashboardDetailColumns.map((col) => col.label);
    const data = [headers, ...rowsToExport.map((row) => (
      dashboardDetailColumns.map((col) => {
        const value = row?.[col.key];
        if (col.key.endsWith('_date') || col.key.includes('_dt')) return formatDate(value);
        return stringifyCell(value);
      })
    ))];
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Assets');
    const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `it-assets-dashboard-${String(ageBucket || 'all')}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [dashboardDetailColumns, filteredInventory, ageBucket]);

  return (
    <>
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body>
          <Row className="g-3 align-items-center">
            <Col>
              <div className="h5 mb-1">Dashboard</div>
              <div className="text-muted small">Allocation and attachment overview.</div>
            </Col>
            <Col className="text-end">
              <Button variant="outline-secondary" size="sm" onClick={load} disabled={loading}>
                <FiRefreshCw className="me-1" />
                Refresh
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={2}>
              <Form.Label>Status</Form.Label>
              <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All</option>
                <option value="Allocated">Allocated</option>
                <option value="In Store">In Store</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label>Location</Form.Label>
              <Form.Select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
                <option value="">All</option>
                {locationOptions.map((v) => <option key={v} value={v}>{v}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label>Asset Type</Form.Label>
              <Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="">All</option>
                {typeOptions.map((v) => <option key={v} value={v}>{v}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label>Age</Form.Label>
              <Form.Select value={ageBucket} onChange={(e) => setAgeBucket(e.target.value)}>
                <option value="">All</option>
                <option value="lt3">0–3 years</option>
                <option value="3to5">3–5 years</option>
                <option value="5to7">5–7 years</option>
                <option value="gt7">7+ years</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label>Attachment</Form.Label>
              <Form.Select value={attachmentFilter} onChange={(e) => setAttachmentFilter(e.target.value)}>
                <option value="">All</option>
                <option value="with">Uploaded</option>
                <option value="without">Not Uploaded</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label>Condition</Form.Label>
              <Form.Select value={conditionFilter} onChange={(e) => setConditionFilter(e.target.value)}>
                <option value="">All</option>
                <option value="Working">Working</option>
                <option value="Not Working">Not Working</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-3">
        <Col md={2}><StatCard icon={<FiBox />} label="Total" value={ageStats.total} tone="primary" /></Col>
        <Col md={2}>
          <StatCard
            icon={<FiUserCheck />}
            label="Allocated"
            value={ageStats.allocated}
            subtitle={ageStats.total ? `${Math.round((ageStats.allocated / ageStats.total) * 100)}% of total` : null}
            tone="warning"
          />
        </Col>
        <Col md={2}>
          <StatCard
            icon={<FiArchive />}
            label="In Store"
            value={ageStats.inStore}
            subtitle={ageStats.total ? `${Math.round((ageStats.inStore / ageStats.total) * 100)}% of total` : null}
            tone="info"
          />
        </Col>
        <Col md={2}>
          <StatCard
            icon={<FiPaperclip />}
            label="With Attachments"
            value={ageStats.withAttachments}
            subtitle={ageStats.total ? `${Math.round((ageStats.withAttachments / ageStats.total) * 100)}% of total` : null}
            tone="success"
          />
        </Col>
        <Col md={2}><StatCard icon={<FiClock />} label="3–5 Years" value={ageStats.count3to5} tone="secondary" /></Col>
        <Col md={2}><StatCard icon={<FiClock />} label="5–7 Years" value={ageStats.count5to7} tone="warning" /></Col>
        <Col md={2}><StatCard icon={<FiClock />} label="7+ Years" value={ageStats.count7plus} tone="danger" /></Col>
        <Col md={2}>
          <StatCard
            icon={<FiAlertTriangle />}
            label="Not Working"
            value={notWorkingCount}
            subtitle={ageStats.total ? `${Math.round((notWorkingCount / ageStats.total) * 100)}% of total` : null}
            tone="danger"
            active={conditionFilter === 'Not Working'}
            onClick={() => setConditionFilter((prev) => (prev === 'Not Working' ? '' : 'Not Working'))}
          />
        </Col>
      </Row>

      <Card className="border-0 shadow-sm mb-3">
        <Card.Body>
          <Row className="align-items-center g-2 mb-3">
            <Col>
              <div className="fw-semibold">Filtered Asset Details</div>
              <div className="text-muted small">View the current dashboard asset selection by age bucket and other filters.</div>
            </Col>
            <Col className="text-end">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={downloadDashboardInventory}
                disabled={!filteredInventory || !filteredInventory.length}
              >
                Download Details
              </Button>
            </Col>
          </Row>
          <div className="table-responsive" style={{ maxHeight: 320, overflowY: 'auto' }}>
            <Table hover size="sm" className="align-middle mb-0 small">
              <thead className="table-light">
                <tr>
                  {dashboardDetailColumns.map((col) => (
                    <th key={col.key} className="text-nowrap">{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(filteredInventory || []).map((row) => (
                  <tr key={`${row.serial_number}-${row.allocation_id || ''}`}>
                    {dashboardDetailColumns.map((col) => {
                      if (col.key === 'status') {
                        const isAllocated = normalizeDropdownValue(normalizeStatusValue(row?.status)) === 'allocated';
                        return (
                          <td key={col.key} className="text-nowrap">
                            <Badge bg={isAllocated ? 'primary' : 'secondary'}>{row?.status || '-'}</Badge>
                          </td>
                        );
                      }
                      if (col.key === 'asset_condition') {
                        const isNotWorking = String(row?.asset_condition || '').trim().toLowerCase() === 'not working';
                        return (
                          <td key={col.key} className="text-nowrap">
                            <Badge bg={isNotWorking ? 'danger' : 'success'}>{row?.asset_condition || 'Working'}</Badge>
                          </td>
                        );
                      }
                      const value = col.key.endsWith('_date') || col.key.includes('_dt') ? formatDate(row?.[col.key]) : stringifyCell(row?.[col.key]);
                      return <td key={col.key} className="text-nowrap">{value}</td>;
                    })}
                  </tr>
                ))}
                {(!filteredInventory || !filteredInventory.length) && (
                  <tr>
                    <td colSpan={dashboardDetailColumns.length} className="text-center text-muted py-4">
                      No asset details match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-3">
        <Col lg={8}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="fw-semibold mb-2">Assets by State</div>
              <IndiaAssetChoropleth attachmentsByLocation={attachmentsByLocation} height={360} />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="fw-semibold mb-2">Top Locations</div>
              <LocationLeaderboard attachmentsByLocation={attachmentsByLocation} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="fw-semibold mb-2">Asset Type Composition</div>
              <AssetTypeDonut data={assetTypeBreakdown} total={ageStats.total} />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="fw-semibold mb-2">Top Faulty Components (Not Working assets)</div>
              <TopFaultyComponents data={topFaultyComponents} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Row className="g-3">
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="fw-semibold mb-2">In Store / Allocated (Attachments)</div>
              {loading ? (
                <div className="d-flex justify-content-center align-items-center py-4">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : (
                <div style={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer>
                    <BarChart data={statusCounts}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="total" name="Total Assets" fill={CHART_COLORS[0]} radius={[6, 6, 0, 0]} />
                      <Bar dataKey="withAttachments" name="Assets With Attachments" fill={CHART_COLORS[1]} radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="fw-semibold mb-2">Asset Age (Allocated vs In Store)</div>
              {loading ? (
                <div className="d-flex justify-content-center align-items-center py-4">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : (
                <div style={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer>
                    <BarChart data={ageBucketChartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="bucket" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="inStore" name="In Store" stackId="a" fill={CHART_COLORS[4]} />
                      <Bar dataKey="allocated" name="Allocated" stackId="a" fill={CHART_COLORS[2]} radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col lg={12}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="fw-semibold mb-2">Attachments by Location</div>
              {loading ? (
                <div className="d-flex justify-content-center align-items-center py-4">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : (
                <div style={{ width: '100%', height: 320 }}>
                  <ResponsiveContainer>
                    <BarChart data={attachmentsByLocation}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="location" interval={0} angle={-20} textAnchor="end" height={70} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="totalAssets" name="Total Assets" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="inStoreAssets" name="In Store (Count)" fill={CHART_COLORS[4]} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="allocatedAssets" name="Allocated (Count)" fill={CHART_COLORS[2]} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="inStoreWithAttachments" name="In Store (Attachments)" fill={CHART_COLORS[1]} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="allocatedWithAttachments" name="Allocated (Attachments)" fill={CHART_COLORS[7]} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="fw-semibold mb-2">Oldest Assets (Currently Allocated)</div>
              <div className="table-responsive">
                <Table hover size="sm" className="align-middle mb-0 small">
                  <thead className="table-light">
                    <tr>
                      <th className="text-nowrap">Age</th>
                      <th className="text-nowrap">Serial</th>
                      <th className="text-nowrap">Genie Code</th>
                      <th className="text-nowrap">Type</th>
                      <th className="text-nowrap">Location</th>
                      <th className="text-nowrap">User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {oldestAssetsAllocated.map(({ row, ageYears }) => (
                      <tr key={`${row.serial_number}-${row.allocation_id || ''}`}>
                        <td className="text-nowrap"><Badge bg={ageYears >= 5 ? 'danger' : (ageYears >= 3 ? 'warning' : 'secondary')}>{ageYears.toFixed(1)}y</Badge></td>
                        <td className="text-nowrap">{row.serial_number}</td>
                        <td className="text-nowrap">{row.genie_asset_code || '-'}</td>
                        <td className="text-nowrap">{row.asset_type || '-'}</td>
                        <td className="text-nowrap">{row.location || '-'}</td>
                        <td className="text-nowrap">{row.alloc_email || row.alloc_emp_id || '-'}</td>
                      </tr>
                    ))}
                    {!oldestAssetsAllocated.length && (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-3">No allocated assets with age data.</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="fw-semibold mb-2">Users With Oldest Assets (Currently Allocated)</div>
              <div className="table-responsive">
                <Table hover size="sm" className="align-middle mb-0 small">
                  <thead className="table-light">
                    <tr>
                      <th className="text-nowrap">User</th>
                      <th className="text-nowrap">Oldest Age</th>
                      <th className="text-nowrap">Oldest Serial</th>
                      <th className="text-nowrap">Allocated Count</th>
                      <th className="text-nowrap">5y+ Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {oldestUsersAllocated.map((u) => (
                      <tr key={u.userKey}>
                        <td className="text-nowrap">{u.userKey}</td>
                        <td className="text-nowrap"><Badge bg={u.oldestAge >= 5 ? 'danger' : (u.oldestAge >= 3 ? 'warning' : 'secondary')}>{Number(u.oldestAge || 0).toFixed(1)}y</Badge></td>
                        <td className="text-nowrap">{u.oldestSerial || '-'}</td>
                        <td className="text-nowrap">{u.count}</td>
                        <td className="text-nowrap">{u.count5plus}</td>
                      </tr>
                    ))}
                    {!oldestUsersAllocated.length && (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-3">No allocated users with age data.</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

const AssetHistoryPanel = ({ canManage, presetAssetId = '', onPresetConsumed }) => {
  const [serialNumber, setSerialNumber] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [event, setEvent] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [shouldAutoLoad, setShouldAutoLoad] = useState(false);

  const historySearchDatalistId = useMemo(() => 'it-assets-history-suggest', []);
  const historySearchSuggestions = useMemo(
    () => buildSearchSuggestions({
      rows,
      keys: ['serial_number', 'genie_asset_code', 'location', 'asset_type', 'manufacture', 'model_details', 'alloc_email', 'alloc_user_name', 'alloc_emp_id'],
      limit: 40
    }),
    [rows]
  );

  const [showAttachments, setShowAttachments] = useState(false);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [activeAllocationId, setActiveAllocationId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewName, setPreviewName] = useState('');
  const [previewMime, setPreviewMime] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewReturnToList, setPreviewReturnToList] = useState(false);

  const roleType = String(getUser()?.roleType || getUser()?.role?.roleType || '').toUpperCase();
  const isAdminRole = roleType === 'ADMIN';

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadXlsx = (xlsxRows) => {
    try {
      auditLogAPI.logClientEvent({
        module: 'it_assets',
        action: 'download_xlsx',
        description: 'Downloaded asset history (xlsx)',
        changedData: {
          assetId: String(serialNumber || '').trim() || null,
          filters: { from: from || null, to: to || null, event: event || null, search: search || null },
          count: (xlsxRows || []).length
        }
      });
    } catch {
      // ignore
    }

    const headers = HISTORY_COLUMNS.map((c) => c.label);
    const aoa = [
      headers,
      ...(xlsxRows || []).map((r) => (
        HISTORY_COLUMNS.map((c) => {
          const raw = r?.[c.key];
          if (c.key.endsWith('_date') || c.key.includes('_dt') || c.key === 'event_date') return formatDate(raw);
          return stringifyCell(raw);
        })
      ))
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'AssetHistory');
    const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    downloadBlob(blob, `it-asset-history-${String(serialNumber || 'asset').trim() || 'asset'}.xlsx`);
  };

  const loadHistory = useCallback(async () => {
    const id = String(serialNumber || '').trim();
    if (!id) {
      setError('Serial Number is required to view history.');
      setRows([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await itAssetsAPI.searchAssetHistory({
        assetId: id,
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(event ? { event } : {})
      });
      setRows(Array.isArray(res?.data) ? res.data : res?.data?.data || []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load asset history.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [serialNumber, from, to, event]);

  useEffect(() => {
    const id = String(presetAssetId || '').trim();
    if (!id) return;
    setSerialNumber(id);
    setFrom('');
    setTo('');
    setEvent('');
    setSearch('');
    setShouldAutoLoad(true);
    if (typeof onPresetConsumed === 'function') onPresetConsumed();
  }, [presetAssetId, onPresetConsumed]);

  useEffect(() => {
    if (!shouldAutoLoad) return;
    setShouldAutoLoad(false);
    loadHistory();
  }, [shouldAutoLoad, loadHistory]);

  const openAttachments = useCallback(async ({ allocationId }) => {
    const id = String(serialNumber || '').trim();
    if (!id) {
      setError('Serial Number is required to view attachments.');
      return;
    }
    setShowAttachments(true);
    setAttachments([]);
    setAttachmentsError('');
    setSelectedFile(null);
    setActiveAllocationId(allocationId ?? null);
    setAttachmentsLoading(true);
    try {
      const res = await itAssetsAPI.listAttachments(id);
      setAttachments(Array.isArray(res?.data) ? res.data : res?.data?.data || []);
    } catch (e) {
      setAttachmentsError(e?.response?.data?.message || e?.message || 'Failed to load attachments.');
    } finally {
      setAttachmentsLoading(false);
    }
  }, [serialNumber]);

  const filteredRows = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return rows || [];
    return (rows || []).filter((r) => {
      const hay = HISTORY_COLUMNS
        .map((c) => r?.[c.key])
        .filter((v) => v !== null && v !== undefined)
        .map((v) => stringifyCell(v))
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search]);

  const filteredAttachments = useMemo(() => {
    const allocId = activeAllocationId;
    if (!allocId) return attachments || [];
    return (attachments || []).filter((a) => Number(a?.allocation_id) === Number(allocId));
  }, [attachments, activeAllocationId]);

  const onUploadAttachment = async () => {
    const id = String(serialNumber || '').trim();
    if (!id) {
      setAttachmentsError('Serial Number is required.');
      return;
    }
    if (!selectedFile) {
      setAttachmentsError('Please choose a file (PDF/PNG/JPEG) to upload.');
      return;
    }
    setUploading(true);
    setAttachmentsError('');
    try {
      const form = new FormData();
      form.append('file', selectedFile);
      if (activeAllocationId) form.append('allocationId', String(activeAllocationId));
      await itAssetsAPI.uploadAttachment(id, form);

      const res = await itAssetsAPI.listAttachments(id);
      setAttachments(Array.isArray(res?.data) ? res.data : res?.data?.data || []);
      setSelectedFile(null);
    } catch (e) {
      setAttachmentsError(e?.response?.data?.message || e?.message || 'Failed to upload attachment.');
    } finally {
      setUploading(false);
    }
  };

  const onDownloadAttachment = async (attachment) => {
    try {
      const res = await itAssetsAPI.downloadAttachment(attachment.id);
      const blob = res instanceof Blob ? res : (res?.data instanceof Blob ? res.data : new Blob([res?.data || '']));
      downloadBlob(blob, attachment.original_name || `attachment-${attachment.id}`);
    } catch (e) {
      setAttachmentsError(e?.response?.data?.message || e?.message || 'Failed to download attachment.');
    }
  };

  const onDeleteAttachment = async (attachment) => {
    if (!isAdminRole) return;
    const ok = globalThis.confirm(`Delete attachment "${attachment.original_name || attachment.id}"?`);
    if (!ok) return;
    setAttachmentsError('');
    try {
      await itAssetsAPI.deleteAttachment(attachment.id);
      const res = await itAssetsAPI.listAttachments(String(serialNumber || '').trim());
      setAttachments(Array.isArray(res?.data) ? res.data : res?.data?.data || []);
    } catch (e) {
      setAttachmentsError(e?.response?.data?.message || e?.message || 'Failed to delete attachment.');
    }
  };

  const closePreview = useCallback(() => {
    setShowPreview(false);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl('');
    setPreviewName('');
    setPreviewMime('');
    setPreviewLoading(false);
    if (previewReturnToList) setShowAttachments(true);
    setPreviewReturnToList(false);
  }, [previewUrl]);

  const onViewAttachment = async (attachment) => {
    setPreviewLoading(true);
    setAttachmentsError('');
    try {
      const res = await itAssetsAPI.downloadAttachment(attachment.id);
      const fallbackMime = attachment?.mime_type || inferMimeFromName(attachment?.original_name);
      const rawBlob = res instanceof Blob ? res : (res?.data instanceof Blob ? res.data : new Blob([res?.data || ''], { type: fallbackMime || 'application/octet-stream' }));
      const blob = ensureBlobType(rawBlob, fallbackMime);
      const url = URL.createObjectURL(blob);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      setPreviewName(attachment.original_name || `attachment-${attachment.id}`);
      setPreviewMime(String(fallbackMime || blob.type || ''));
      // Avoid stacking modals (can cause blank screen/backdrop issues).
      setPreviewReturnToList(true);
      setShowAttachments(false);
      setShowPreview(true);
    } catch (e) {
      setAttachmentsError(e?.response?.data?.message || e?.message || 'Failed to view attachment.');
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <>
      <Card className="border-0 shadow-sm">
        <Card.Body>
          <Row className="g-3 align-items-end">
	            <Col md={3}>
	              <Form.Label>Serial Number *</Form.Label>
	              <Form.Control value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} placeholder="Enter Serial Number" />
	            </Col>
            <Col md={2}>
              <Form.Label>From</Form.Label>
              <Form.Control type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Col>
            <Col md={2}>
              <Form.Label>To</Form.Label>
              <Form.Control type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Col>
            <Col md={2}>
              <Form.Label>Event</Form.Label>
              <Form.Select value={event} onChange={(e) => setEvent(e.target.value)}>
                <option value="">All</option>
                <option value="Allocated">Allocated</option>
                <option value="In Store">In Store</option>
              </Form.Select>
            </Col>
            <Col md={3} className="text-md-end">
              <div className="d-flex justify-content-between justify-content-md-end gap-2">
                <Button variant="outline-secondary" onClick={() => downloadXlsx(filteredRows)} disabled={!filteredRows.length}>
                  Download
                </Button>
                <Button variant="primary" onClick={loadHistory} disabled={loading || !canManage}>
                  {loading ? 'Loading…' : 'Load History'}
                </Button>
              </div>
            </Col>

	            <Col md={12}>
	              <Form.Label>Search</Form.Label>
	              <Form.Control
	                value={search}
	                onChange={(e) => setSearch(e.target.value)}
	                placeholder="Search any field…"
	                list={historySearchDatalistId}
	              />
	              <datalist id={historySearchDatalistId}>
	                {historySearchSuggestions.map((v) => (
	                  <option key={v} value={v} />
	                ))}
	              </datalist>
	            </Col>
          </Row>

          {error && <Alert variant="danger" className="mt-3" dismissible onClose={() => setError('')}>{error}</Alert>}

          <div className="table-responsive mt-3">
            <Table hover size="sm" className="align-middle mb-0 small">
              <thead className="table-light">
                <tr>
                  {HISTORY_COLUMNS.map((c) => (
                    <th key={c.key} className="text-nowrap">{c.label}</th>
                  ))}
                  <th className="text-nowrap">Attachment</th>
                </tr>
              </thead>
              <tbody>
	                {filteredRows.map((r) => (
	                  <tr key={`${r.serial_number}-${r.allocation_id ?? r.event_date ?? Math.random()}`}>
	                    {HISTORY_COLUMNS.map((c) => {
	                      const raw = r?.[c.key];
	                      const value =
	                        (c.key.endsWith('_date') || c.key.includes('_dt') || c.key === 'event_date' || c.key === 'event_timestamp')
	                            ? formatDate(raw)
	                            : stringifyCell(raw);
                      if (c.key === 'allocation_status') {
                        return (
                          <td key={c.key} className="text-nowrap">
                            <Badge bg="secondary">{raw ? normalizeStatusValue(raw) : '-'}</Badge>
                          </td>
                        );
                      }
                      if (c.key === 'event_type') {
                        return (
                          <td key={c.key} className="text-nowrap">
                            <Badge bg={String(raw || '').toLowerCase() === 'in store' ? 'info' : 'primary'}>{value}</Badge>
                          </td>
                        );
                      }
                      return <td key={c.key} className="text-nowrap">{value}</td>;
                    })}
                    <td className="text-nowrap">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => openAttachments({ allocationId: r?.allocation_id })}
                        disabled={!String(serialNumber || '').trim()}
                      >
                        Attachment ({Number(r?.attachment_count || 0) || 0})
                      </Button>
                    </td>
                  </tr>
                ))}

                {!filteredRows.length && (
                  <tr>
                    <td colSpan={HISTORY_COLUMNS.length + 1} className="text-center text-muted py-4">
                      No history found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <Modal show={showAttachments} onHide={() => setShowAttachments(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Attachments</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {attachmentsError && <Alert variant="danger" dismissible onClose={() => setAttachmentsError('')}>{attachmentsError}</Alert>}
          <div className="d-flex flex-column gap-3">
            <div>
              <div className="text-muted small mb-1">
	                Asset: <span className="fw-semibold">{String(serialNumber || '').trim()}</span>
              </div>
              <Form.Control type="file" accept="image/png,image/jpeg,application/pdf" onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} />
              <div className="d-flex justify-content-end mt-2">
                <Button variant="primary" onClick={onUploadAttachment} disabled={uploading || !selectedFile}>
                  {uploading ? 'Uploading…' : 'Upload'}
                </Button>
              </div>
            </div>

            {attachmentsLoading ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" /> Loading…
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover size="sm" className="align-middle mb-0 small">
                  <thead className="table-light">
	                    <tr>
	                      <th className="text-nowrap">Uploaded At</th>
	                      <th className="text-nowrap">File</th>
	                      <th className="text-nowrap">Action</th>
	                    </tr>
	                  </thead>
	                  <tbody>
	                    {filteredAttachments.map((a) => (
	                      <tr key={a.id}>
	                        <td className="text-nowrap">{formatDate(a.uploaded_at)}</td>
	                        <td className="text-nowrap">{a.original_name || `attachment-${a.id}`}</td>
	                        <td className="text-nowrap">
	                          <div className="d-flex gap-2">
	                            <Button size="sm" variant="outline-primary" onClick={() => onViewAttachment(a)} disabled={previewLoading}>
	                              View
                            </Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => onDownloadAttachment(a)}>
                              Download
                            </Button>
                            {isAdminRole && (
                              <Button size="sm" variant="outline-danger" onClick={() => onDeleteAttachment(a)}>
                                Delete
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
	                    {!filteredAttachments.length && (
	                      <tr>
	                        <td colSpan={3} className="text-center text-muted py-3">
	                          No attachments found.
	                        </td>
	                      </tr>
	                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>

      {/* Attachment preview modal */}
      <Modal show={showPreview} onHide={closePreview} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{previewName || 'Preview'}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: 520 }}>
          {previewLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="sm" /> Loading…
            </div>
          ) : !previewUrl ? (
            <div className="text-center text-muted py-5">No preview available.</div>
          ) : String(previewMime || '').toLowerCase().startsWith('image/') ? (
            <div className="d-flex justify-content-center">
              <img src={previewUrl} alt={previewName || 'Attachment'} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
            </div>
          ) : (
            <iframe title="Attachment Preview" src={previewUrl} style={{ width: '100%', height: '70vh', border: 0 }} />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closePreview}>Close</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const AUDIT_COLUMNS = [
  { key: 'serial_number', label: 'Serial Number' },
  { key: 'genie_asset_code', label: 'Genie Asset Code' },
  { key: 'asset_type', label: 'Asset Type' },
  { key: 'location', label: 'Location' },
  // Prefer server-computed `allocation_status` (current allocation based).
  // Fallback to older field names for backward compatibility until backend is restarted.
  { key: 'allocation_status', label: 'Status' },
  // removed: alloc_last_deallocation_date
  { key: 'last_audited_at', label: 'Last Audited At' },
  { key: 'last_audited_by_email', label: 'Last Audited By' },
  { key: 'last_audit_remarks', label: 'Last Audit Remarks' }
];

				const AssetAuditPanel = ({ canManage, onAssetIdClick }) => {
		  const [status, setStatus] = useState('');
		  const [q, setQ] = useState('');
	  const computeFyYearStart = useCallback((date) => {
	    const d = new Date(date);
	    if (Number.isNaN(d.getTime())) return null;
	    const y = d.getFullYear();
	    const m = d.getMonth() + 1;
	    return m >= 4 ? y : y - 1;
	  }, []);
	  const computeFyQuarter = useCallback((date) => {
	    const d = new Date(date);
	    if (Number.isNaN(d.getTime())) return null;
	    const m = d.getMonth() + 1;
	    if (m >= 4 && m <= 6) return 1;
	    if (m >= 7 && m <= 9) return 2;
	    if (m >= 10 && m <= 12) return 3;
	    return 4;
	  }, []);

	  const [auditYear, setAuditYear] = useState(() => computeFyYearStart(new Date()) || new Date().getFullYear());
	  const [auditQuarter, setAuditQuarter] = useState(() => String(computeFyQuarter(new Date()) || 1));
	  const auditYearOptions = useMemo(() => {
	    const current = computeFyYearStart(new Date()) || new Date().getFullYear();
	    return [current, current - 1, current - 2, current - 3, current - 4];
	  }, [computeFyYearStart]);

	  const [loading, setLoading] = useState(false);
	  const [error, setError] = useState('');
		  const [rows, setRows] = useState([]);
	  const auditSearchDatalistId = useMemo(() => 'it-assets-audit-suggest', []);
	  const auditSearchSuggestions = useMemo(
	    () => buildSearchSuggestions({
	      rows,
	      keys: ['serial_number', 'genie_asset_code', 'location', 'asset_type', 'manufacture', 'model_details'],
	      limit: 40
	    }),
	    [rows]
	  );

  const [showAudit, setShowAudit] = useState(false);
	  const [activeAssetId, setActiveAssetId] = useState('');
		  const [auditRemarks, setAuditRemarks] = useState('');
		  const [physicalAudit, setPhysicalAudit] = useState('');
		  const [auditAssetCondition, setAuditAssetCondition] = useState('');
		  const [savingAudit, setSavingAudit] = useState(false);
		  const [auditHistoryLoading, setAuditHistoryLoading] = useState(false);
		  const [auditHistoryError, setAuditHistoryError] = useState('');
		  const [auditHistoryRows, setAuditHistoryRows] = useState([]);

  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [activeAuditId, setActiveAuditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePickerKey, setFilePickerKey] = useState(0);

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadAuditXlsx = () => {
    try {
      const fyLabel = `${auditYear}-${String(Number(auditYear) + 1).slice(-2)}`;
      auditLogAPI.logClientEvent({
        module: 'it_assets',
        action: 'download_xlsx',
        description: 'Downloaded it-assets-audit-queue (xlsx)',
        changedData: {
          exportName: 'it-assets-audit-queue',
          filters: {
            status: String(status || '').trim() || null,
            q: String(q || '').trim() || null,
            auditYear: Number(auditYear) || null,
            auditQuarter: String(auditQuarter || '').trim() || null,
            financialYear: fyLabel
          },
          count: (rows || []).length
        }
      });
    } catch {
      // ignore
    }

    const headers = AUDIT_COLUMNS.map((c) => c.label);
    const aoa = [
      headers,
      ...(rows || []).map((r) => (
        AUDIT_COLUMNS.map((c) => {
          const raw = c.key === 'allocation_status'
            ? (r?.allocation_status ?? r?.audit_queue_status ?? null)
            : r?.[c.key];
          if (c.key.endsWith('_date') || c.key.includes('_dt') || String(c.key).includes('audited')) return formatDate(raw);
          return stringifyCell(raw);
        })
      ))
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'AuditQueue');
    const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const fyLabel = `${auditYear}-${String(Number(auditYear) + 1).slice(-2)}`;
    downloadBlob(blob, `it-assets-audit-queue_${fyLabel}_${auditQuarter === 'all' ? 'ALL' : `Q${auditQuarter}`}.xlsx`);
  };

			  const loadQueue = useCallback(async () => {
			    setLoading(true);
			    setError('');
			    try {
	      const res = await itAssetsAPI.listAuditQueue({
	        ...(status ? { status } : {}),
	        ...(q ? { q } : {}),
	        auditYear,
	        ...(auditQuarter && auditQuarter !== 'all' ? { auditQuarter } : { auditQuarter: 'all' })
	      });
	      setRows(Array.isArray(res?.data) ? res.data : res?.data?.data || []);
	    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load audit queue.');
      setRows([]);
    } finally {
      setLoading(false);
    }
	  }, [status, q, auditYear, auditQuarter]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

			  const openAudit = (row) => {
			    const assetId = String(row?.serial_number || '').trim();
			    setActiveAssetId(assetId);
			    setAuditRemarks('');
			    setPhysicalAudit('');
			    setAuditAssetCondition('');
			    setActiveAuditId(null);
			    setAttachments([]);
			    setAttachmentsError('');
			    setPendingFiles([]);
			    setSelectedFiles([]);
			    setFilePickerKey((k) => k + 1);
			    setAuditHistoryRows([]);
			    setAuditHistoryError('');
			    setShowAudit(true);

			    if (assetId) {
			      setAuditHistoryLoading(true);
			      itAssetsAPI.listAssetAudits(assetId)
			        .then((res) => {
			          const data = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
			          setAuditHistoryRows(Array.isArray(data) ? data : []);
			        })
			        .catch((e) => {
			          setAuditHistoryError(e?.response?.data?.message || e?.message || 'Failed to load audit history.');
			          setAuditHistoryRows([]);
			        })
			        .finally(() => setAuditHistoryLoading(false));
			    }
			  };

		  const saveAudit = async () => {
	    const assetId = String(activeAssetId || '').trim();
	    if (!assetId) return;
	    setSavingAudit(true);
	    setError('');
		    try {
			      if (String(auditQuarter) === 'all') {
			        throw new Error('Please select a Quarter (Q1–Q4) before saving an audit.');
			      }
			      const res = await itAssetsAPI.createAssetAudit(assetId, {
			        remarks: auditRemarks,
			        physicalAudit,
			        auditAssetCondition,
			        auditYear,
			        auditQuarter: Number(auditQuarter)
			      });
	      const auditId = res?.data?.id ?? res?.data?.data?.id ?? null;
	      setActiveAuditId(auditId ? Number(auditId) : null);
		      await loadQueue();

		      // Upload pending attachments (if any) after audit is created, to link to auditId.
		      if (auditId && pendingFiles.length) {
		        setUploading(true);
		        try {
		          for (const file of pendingFiles) {
		            const form = new FormData();
		            form.append('file', file);
		            form.append('auditId', String(auditId));
		            await itAssetsAPI.uploadAttachment(assetId, form);
		          }
		          setPendingFiles([]);
		        } finally {
		          setUploading(false);
		        }
		      }

		      // Load attachments after saving/uploading
		      if (auditId) await openAuditAttachments({ assetId, auditId });
	    } catch (e) {
	      setError(e?.response?.data?.message || e?.message || 'Failed to save audit.');
	    } finally {
	      setSavingAudit(false);
	    }
	  };

  const openAuditAttachments = useCallback(async ({ assetId, auditId }) => {
    const id = String(assetId || '').trim();
    if (!id) return;
    setAttachments([]);
    setAttachmentsError('');
    setSelectedFiles([]);
    setFilePickerKey((k) => k + 1);
    setActiveAuditId(Number(auditId) || null);
    setAttachmentsLoading(true);
    try {
      const res = await itAssetsAPI.listAttachments(id, auditId ? { auditId } : undefined);
      setAttachments(Array.isArray(res?.data) ? res.data : res?.data?.data || []);
    } catch (e) {
      setAttachmentsError(e?.response?.data?.message || e?.message || 'Failed to load attachments.');
    } finally {
      setAttachmentsLoading(false);
    }
  }, []);

  const addPendingAttachments = () => {
    const next = Array.isArray(selectedFiles) ? selectedFiles.filter(Boolean) : [];
    if (!next.length) {
      setAttachmentsError('Please choose a file (PDF/PNG/JPEG) to add.');
      return;
    }
    setAttachmentsError('');
    setPendingFiles((prev) => [...prev, ...next]);
    setSelectedFiles([]);
    setFilePickerKey((k) => k + 1);
  };

  const downloadAttachment = async (attachment) => {
    try {
      const res = await itAssetsAPI.downloadAttachment(attachment.id);
      const blob = res?.data instanceof Blob ? res.data : new Blob([res?.data || '']);
      downloadBlob(blob, attachment.original_name || `attachment-${attachment.id}`);
    } catch (e) {
      setAttachmentsError(e?.response?.data?.message || e?.message || 'Failed to download attachment.');
    }
  };

	      return (
	        <>
	      <Card className="border-0 shadow-sm">
	        <Card.Body>
		          <Row className="g-3 align-items-end">
		            <Col md={2}>
		              <Form.Label>Status</Form.Label>
			              <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
			                <option value="">All</option>
			                <option value="In Store">In Store</option>
			              </Form.Select>
		            </Col>
		            <Col md={2}>
		              <Form.Label>FY</Form.Label>
		              <Form.Select value={auditYear} onChange={(e) => setAuditYear(Number(e.target.value) || auditYear)}>
		                {auditYearOptions.map((y) => (
		                  <option key={y} value={y}>{`${y}-${String(y + 1).slice(-2)}`}</option>
		                ))}
		              </Form.Select>
		            </Col>
			            <Col md={2}>
			              <Form.Label>Quarter</Form.Label>
		              <Form.Select value={auditQuarter} onChange={(e) => setAuditQuarter(String(e.target.value))}>
		                <option value="all">All</option>
		                <option value={1}>Q1 (Apr–Jun)</option>
		                <option value={2}>Q2 (Jul–Sep)</option>
		                <option value={3}>Q3 (Oct–Dec)</option>
		                <option value={4}>Q4 (Jan–Mar)</option>
		              </Form.Select>
		            </Col>
				            <Col md={6}>
				              <Form.Label>Search</Form.Label>
						              <div className="d-flex gap-2">
						                <Form.Control
						                  value={q}
						                  onChange={(e) => setQ(e.target.value)}
						                  placeholder="Search Serial Number / code / type / location…"
						                  list={auditSearchDatalistId}
						                />
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={downloadAuditXlsx}
                              disabled={loading || !canManage || !(rows || []).length}
                            >
                              Download
                            </Button>
						              </div>
				              <datalist id={auditSearchDatalistId}>
				                {auditSearchSuggestions.map((v) => (
				                  <option key={v} value={v} />
			                ))}
			              </datalist>
		            </Col>
			          </Row>

          {error && <Alert variant="danger" className="mt-3" dismissible onClose={() => setError('')}>{error}</Alert>}

          <div className="table-responsive mt-3">
            <Table hover size="sm" className="align-middle mb-0 small">
              <thead className="table-light">
                <tr>
                  {AUDIT_COLUMNS.map((c) => (
                    <th key={c.key} className="text-nowrap">{c.label}</th>
                  ))}
                  <th className="text-nowrap">Action</th>
                </tr>
              </thead>
              <tbody>
	                {(rows || []).map((r) => (
	                  <tr key={r.serial_number}>
	                  {AUDIT_COLUMNS.map((c) => {
	                      const raw = c.key === 'allocation_status'
	                        ? (r?.allocation_status ?? r?.audit_queue_status ?? null)
	                        : r?.[c.key];
	                      const value = c.key.endsWith('_date') || c.key.includes('_dt') || String(c.key).includes('audited') ? formatDate(raw) : stringifyCell(raw);
		                      if (c.key === 'serial_number' && typeof onAssetIdClick === 'function' && r?.serial_number) {
		                        return (
		                          <td key={c.key} className="text-nowrap">
		                            <Button
		                              variant="link"
		                              size="sm"
		                              className="p-0"
		                              onClick={() => onAssetIdClick(String(r.serial_number))}
		                              title="Open asset history"
		                            >
		                              {value}
		                            </Button>
		                          </td>
		                        );
		                      }
	                      if (c.key === 'allocation_status') {
	                        return (
	                          <td key={c.key} className="text-nowrap">
	                            <Badge bg="secondary">{raw ? normalizeStatusValue(raw) : '-'}</Badge>
                          </td>
                        );
                      }
                      return <td key={c.key} className="text-nowrap">{value}</td>;
                    })}
                    <td className="text-nowrap">
                      <div className="d-flex gap-2">
                        <Button size="sm" variant="primary" onClick={() => openAudit(r)}>Audit</Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!rows?.length && (
                  <tr>
                    <td colSpan={AUDIT_COLUMNS.length + 1} className="text-center text-muted py-4">
                      No assets found in audit queue.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

	      <Modal show={showAudit} onHide={() => setShowAudit(false)} centered>
		        <Modal.Header closeButton>
		          <Modal.Title>Asset Audit</Modal.Title>
		        </Modal.Header>
		        <Modal.Body>
		          <div className="text-muted small mb-2">
		            Asset: <span className="fw-semibold">{activeAssetId || '-'}</span>
		          </div>
		          <div className="text-muted small mb-3">
		            Audit Cycle: <span className="fw-semibold">{`${auditYear}-${String(Number(auditYear) + 1).slice(-2)} Q${auditQuarter}`}</span>
		          </div>
		          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
		          {attachmentsError && <Alert variant="danger" dismissible onClose={() => setAttachmentsError('')}>{attachmentsError}</Alert>}
		          {auditHistoryError && <Alert variant="danger" dismissible onClose={() => setAuditHistoryError('')}>{auditHistoryError}</Alert>}
	          <Row className="g-3">
	            <Col md={6}>
	              <Form.Label>Physical Audit</Form.Label>
	              <Form.Select value={physicalAudit} onChange={(e) => setPhysicalAudit(e.target.value)}>
	                <option value="">Select</option>
	                <option value="Yes">Yes</option>
	                <option value="No">No</option>
	              </Form.Select>
	            </Col>
	            <Col md={6}>
	              <Form.Label>Asset Condition</Form.Label>
	              <Form.Select value={auditAssetCondition} onChange={(e) => setAuditAssetCondition(e.target.value)}>
	                <option value="">Select</option>
	                <option value="Good">Good</option>
	                <option value="Average">Average</option>
	                <option value="Not fit for use">Not fit for use</option>
	              </Form.Select>
	            </Col>
	            <Col md={12}>
	              <Form.Label>Remarks</Form.Label>
	              <Form.Control as="textarea" rows={3} value={auditRemarks} onChange={(e) => setAuditRemarks(e.target.value)} placeholder="Audit remarks / findings…" />
	            </Col>
	          </Row>

	          <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="fw-semibold">Audit Attachments</div>
              {activeAuditId ? <div className="text-muted small">Audit ID: {activeAuditId}</div> : null}
            </div>

	            <>
	              <Form.Control
	                key={filePickerKey}
	                type="file"
	                multiple
	                accept="image/png,image/jpeg,application/pdf"
	                onChange={(e) => setSelectedFiles(Array.from(e.target.files || []))}
	              />
	              <div className="d-flex justify-content-end mt-2">
	                <Button
	                  variant="outline-primary"
	                  onClick={addPendingAttachments}
	                  disabled={uploading || !(selectedFiles || []).length}
	                >
	                  Add Attachment
	                </Button>
	              </div>

	              {pendingFiles.length ? (
	                <div className="mt-2 small text-muted">
	                  Pending (will upload on “Save Audit”): {pendingFiles.map((f) => f?.name).filter(Boolean).join(', ')}
	                </div>
	              ) : (
	                <div className="mt-2 small text-muted">No pending attachments.</div>
	              )}

	              {activeAuditId ? (
	                <div className="mt-3">
	                  {attachmentsLoading ? (
	                    <div className="text-center py-3">
	                      <Spinner animation="border" size="sm" /> Loading…
	                    </div>
	                  ) : (
	                    <div className="table-responsive">
	                      <Table hover size="sm" className="align-middle mb-0 small">
	                        <thead className="table-light">
	                          <tr>
	                            <th className="text-nowrap">Uploaded At</th>
	                            <th className="text-nowrap">File</th>
	                            <th className="text-nowrap">Action</th>
	                          </tr>
	                        </thead>
	                        <tbody>
	                          {(attachments || []).map((a) => (
	                            <tr key={a.id}>
	                              <td className="text-nowrap">{formatDate(a.uploaded_at)}</td>
	                              <td className="text-nowrap">{a.original_name || `attachment-${a.id}`}</td>
	                              <td className="text-nowrap">
	                                <Button size="sm" variant="outline-secondary" onClick={() => downloadAttachment(a)}>
	                                  Download
	                                </Button>
	                              </td>
	                            </tr>
	                          ))}
	                          {!attachments?.length && (
	                            <tr>
	                              <td colSpan={3} className="text-center text-muted py-3">
	                                No attachments yet.
	                              </td>
	                            </tr>
	                          )}
	                        </tbody>
	                      </Table>
	                    </div>
	                  )}
	                </div>
	              ) : null}
	            </>
	          </div>

	          <div className="mt-4">
	            <div className="fw-semibold mb-2">Audit History (Quarter-wise)</div>
	            {auditHistoryLoading ? (
	              <div className="text-center py-3">
	                <Spinner animation="border" size="sm" /> Loading…
	              </div>
	            ) : (
	              <div className="table-responsive">
	                <Table hover size="sm" className="align-middle mb-0 small">
	                  <thead className="table-light">
	                    <tr>
	                      <th className="text-nowrap">FY</th>
	                      <th className="text-nowrap">Quarter</th>
	                      <th className="text-nowrap">Audited At</th>
	                      <th className="text-nowrap">Audited By</th>
	                      <th className="text-nowrap">Physical Audit</th>
	                      <th className="text-nowrap">Asset Condition</th>
	                      <th className="text-nowrap">Remarks</th>
	                    </tr>
	                  </thead>
	                  <tbody>
	                    {(auditHistoryRows || []).map((r) => {
	                      const fy = r?.audit_year ? `${r.audit_year}-${String(Number(r.audit_year) + 1).slice(-2)}` : '-';
	                      const qLabel = r?.audit_quarter ? `Q${r.audit_quarter}` : '-';
	                      const pa = r?.physical_audit === 1 || String(r?.physical_audit).toLowerCase() === 'yes' ? 'Yes'
	                        : (r?.physical_audit === 0 || String(r?.physical_audit).toLowerCase() === 'no' ? 'No' : '-');
	                      return (
	                        <tr key={r.id}>
	                          <td className="text-nowrap">{fy}</td>
	                          <td className="text-nowrap">{qLabel}</td>
	                          <td className="text-nowrap">{formatDate(r.audited_at)}</td>
	                          <td className="text-nowrap">{r.audited_by_email || '-'}</td>
	                          <td className="text-nowrap">{pa}</td>
	                          <td className="text-nowrap">{r.audit_asset_condition || '-'}</td>
	                          <td>{r.remarks || '-'}</td>
	                        </tr>
	                      );
	                    })}
	                    {!auditHistoryRows?.length && (
	                      <tr>
	                        <td colSpan={7} className="text-center text-muted py-3">
	                          No audit history found for this asset.
	                        </td>
	                      </tr>
	                    )}
	                  </tbody>
	                </Table>
	              </div>
	            )}
	          </div>
	        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAudit(false)}>Cancel</Button>
          <Button variant="primary" onClick={saveAudit} disabled={savingAudit || !String(activeAssetId || '').trim()}>
            {savingAudit ? 'Saving…' : 'Save Audit'}
          </Button>
        </Modal.Footer>
      </Modal>

	    </>
	  );
};

const UserHistoryPanel = ({ canManage, onAssetIdClick, presetQ = '', onPresetConsumed }) => {
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [shouldAutoLoad, setShouldAutoLoad] = useState(false);

  const datalistId = useMemo(() => 'it-assets-user-history-suggest', []);
  const suggestions = useMemo(
    () => buildSearchSuggestions({ rows, keys: USER_HISTORY_COLUMNS.map((c) => c.key), limit: 40 }),
    [rows]
  );

  const loadUserHistory = useCallback(async () => {
    const qq = String(q || '').trim();
    if (!qq) {
      setError('User search is required (email / emp id / name).');
      setRows([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await itAssetsAPI.searchUserHistory({
        q: qq,
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
      });
      setRows(Array.isArray(res?.data) ? res.data : res?.data?.data || []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load user history.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [q, from, to]);

  useEffect(() => {
    const next = String(presetQ || '').trim();
    if (!next) return;
    setQ(next);
    setFrom('');
    setTo('');
    setSearch('');
    setShouldAutoLoad(true);
    if (typeof onPresetConsumed === 'function') onPresetConsumed();
  }, [presetQ, onPresetConsumed]);

  useEffect(() => {
    if (!shouldAutoLoad) return;
    setShouldAutoLoad(false);
    loadUserHistory();
  }, [shouldAutoLoad, loadUserHistory]);

  const filteredRows = useMemo(() => {
    const needle = String(search || '').trim().toLowerCase();
    if (!needle) return rows || [];
    return (rows || []).filter((r) => {
      const hay = USER_HISTORY_COLUMNS
        .map((c) => r?.[c.key])
        .filter((v) => v !== null && v !== undefined)
        .map((v) => stringifyCell(v))
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, search]);

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadXlsx = (xlsxRows) => {
    try {
      auditLogAPI.logClientEvent({
        module: 'it_assets',
        action: 'download_xlsx',
        description: 'Downloaded user history (xlsx)',
        changedData: {
          q: String(q || '').trim() || null,
          filters: { from: from || null, to: to || null, search: search || null },
          count: (xlsxRows || []).length,
        }
      });
    } catch {
      // ignore
    }

    const headers = USER_HISTORY_COLUMNS.map((c) => c.label);
    const aoa = [
      headers,
      ...(xlsxRows || []).map((r) => (
        USER_HISTORY_COLUMNS.map((c) => {
          const raw = r?.[c.key];
          if (c.key.endsWith('_date') || c.key.includes('_dt') || c.key === 'event_timestamp') return formatDate(raw);
          return stringifyCell(raw);
        })
      ))
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'UserHistory');
    const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    downloadBlob(blob, `it-user-history-${String(q || 'user').trim() || 'user'}.xlsx`);
  };

		  return (
		    <Card className="border-0 shadow-sm">
		      <Card.Body>
        <Row className="g-3 align-items-end">
          <Col md={4}>
            <Form.Label>User (Email / Emp ID / Name) *</Form.Label>
            <Form.Control value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search user…" />
          </Col>
          <Col md={2}>
            <Form.Label>From</Form.Label>
            <Form.Control type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Col>
          <Col md={2}>
            <Form.Label>To</Form.Label>
            <Form.Control type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Col>
		          <Col md={4} className="text-md-end">
		            <div className="d-flex justify-content-between justify-content-md-end gap-2">
                  <Button variant="outline-secondary" onClick={() => downloadXlsx(filteredRows)} disabled={!filteredRows.length}>
                    Download
                  </Button>
		              <Button variant="primary" onClick={loadUserHistory} disabled={loading || !canManage}>
		                {loading ? 'Loading…' : 'Load History'}
		              </Button>
		            </div>
		          </Col>

          <Col md={12}>
            <Form.Label>Search</Form.Label>
            <Form.Control
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search any field…"
              list={datalistId}
            />
            <datalist id={datalistId}>
              {suggestions.map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
          </Col>
        </Row>

        {error && <Alert variant="danger" className="mt-3" dismissible onClose={() => setError('')}>{error}</Alert>}

        <div className="table-responsive mt-3">
          <Table hover size="sm" className="align-middle mb-0 small">
            <thead className="table-light">
              <tr>
                {USER_HISTORY_COLUMNS.map((c) => (
                  <th key={c.key} className="text-nowrap">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => (
                <tr key={`${r.serial_number}-${r.allocation_id ?? r.event_timestamp ?? Math.random()}`}>
                  {USER_HISTORY_COLUMNS.map((c) => {
                    const raw = r?.[c.key];
                    const value =
                      (c.key.endsWith('_date') || c.key.includes('_dt') || c.key === 'event_timestamp')
                        ? formatDate(raw)
                        : stringifyCell(raw);
                    if (c.key === 'serial_number' && typeof onAssetIdClick === 'function') {
                      return (
                        <td key={c.key} className="text-nowrap">
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0"
                            onClick={() => onAssetIdClick(String(r.serial_number))}
                            title="Open asset history"
                          >
                            {value}
                          </Button>
                        </td>
                      );
                    }
                    return <td key={c.key} className="text-nowrap">{value}</td>;
                  })}
                </tr>
              ))}
              {!filteredRows.length && (
                <tr>
                  <td colSpan={USER_HISTORY_COLUMNS.length} className="text-center text-muted py-4">
                    No history found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
};

const ITAssetManagementPage = ({ initialTab = 'my' }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [myAssets, setMyAssets] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [lookups, setLookups] = useState({});
  const [canManage, setCanManage] = useState(false);

  // The URL's `tab` query param is the source of truth for which tab is showing,
  // so browser Back/Forward can step through tab history instead of skipping it
  // entirely (previously activeTab was pure component state, invisible to the URL).
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTabState] = useState(() => searchParams.get('tab') || 'my');
  const hasSettledTabRef = useRef(false);

  const setActiveTab = useCallback((tab, { replace = false } = {}) => {
    setActiveTabState(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    }, { replace });
  }, [setSearchParams]);

  // Adopt tab changes coming FROM the URL (browser Back/Forward, or a shared/bookmarked link).
  // Uses the plain state setter, not the wrapper above, so it doesn't re-push a duplicate entry.
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    if (urlTab && urlTab !== activeTab) {
      setActiveTabState(urlTab);
    }
  }, [searchParams]);
  const [historyPresetAssetId, setHistoryPresetAssetId] = useState('');
  const consumeHistoryPreset = useCallback(() => setHistoryPresetAssetId(''), []);
  const [userHistoryPresetQ, setUserHistoryPresetQ] = useState('');
  const consumeUserHistoryPreset = useCallback(() => setUserHistoryPresetQ(''), []);

  const roleType = String(getUser()?.roleType || getUser()?.role?.roleType || '').toUpperCase();
  // Be tolerant to different user object shapes in localStorage.
  const isAdminRole = roleType === 'ADMIN' || String(getUser()?.role?.roleType || '').toUpperCase() === 'ADMIN';
  const canApprove = roleType === 'ADMIN' || roleType === 'IT_HEAD';
  const canRequest = roleType === 'EDITOR';

	  const openAssetHistory = useCallback((serialNumber) => {
	    const serial = String(serialNumber || '').trim();
	    if (!serial) return;
	    setHistoryPresetAssetId(serial);
	    setActiveTab('history');
	  }, []);

  const openUserHistory = useCallback((value) => {
    const v = String(value || '').trim();
    if (!v) return;
    setUserHistoryPresetQ(v);
    setActiveTab('user_history');
  }, []);

  const [showMyRequests, setShowMyRequests] = useState(false);
  const [myRequestsLoading, setMyRequestsLoading] = useState(false);
  const [myRequestRows, setMyRequestRows] = useState([]);

  // Bulk Excel import modal state (ADMIN only)
  const [showImportExcel, setShowImportExcel] = useState(false);
  const [importExcelFile, setImportExcelFile] = useState(null);
  const [importExcelSubmitting, setImportExcelSubmitting] = useState(false);
  const [importExcelResult, setImportExcelResult] = useState(null);
  const [importExcelError, setImportExcelError] = useState('');

  // Current Allocation attachments modal state (PDF/PNG/JPEG)
  const [showInventoryAttachments, setShowInventoryAttachments] = useState(false);
  const [inventoryAttachmentsLoading, setInventoryAttachmentsLoading] = useState(false);
  const [inventoryAttachmentsError, setInventoryAttachmentsError] = useState('');
  const [inventoryAttachments, setInventoryAttachments] = useState([]);
  const [inventoryAttachmentAssetId, setInventoryAttachmentAssetId] = useState('');
  const [inventoryAttachmentAllocationId, setInventoryAttachmentAllocationId] = useState(null);
  const [inventoryUploading, setInventoryUploading] = useState(false);
  const [inventorySelectedFile, setInventorySelectedFile] = useState(null);
  const [showInventoryAttachmentPreview, setShowInventoryAttachmentPreview] = useState(false);
  const [inventoryPreviewUrl, setInventoryPreviewUrl] = useState('');
  const [inventoryPreviewName, setInventoryPreviewName] = useState('');
  const [inventoryPreviewMime, setInventoryPreviewMime] = useState('');
  const [inventoryPreviewLoading, setInventoryPreviewLoading] = useState(false);
  const [inventoryPreviewReturnToList, setInventoryPreviewReturnToList] = useState(false);
  const [inventoryPreviewScale, setInventoryPreviewScale] = useState(1);

  const downloadBlobLocal = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

	  const osDetailsOptions = useMemo(
	    () => (Array.isArray(lookups?.os_details) && lookups.os_details.length ? lookups.os_details : uniqueValuesByNormalized(inventory, 'os_details')),
	    [inventory, lookups]
	  );

  const cpuOptions = useMemo(
    () => (Array.isArray(lookups?.cpu) && lookups.cpu.length ? lookups.cpu : uniqueValuesByNormalized(inventory, 'cpu')),
    [inventory, lookups]
  );

  const ramSizeOptions = useMemo(() => ['4', '8', '12', '16', '24', '32', '40'], []);

  const assetTypeOptions = useMemo(
    () => (Array.isArray(lookups?.asset_type) && lookups.asset_type.length ? lookups.asset_type : uniqueValuesByNormalized(inventory, 'asset_type')),
    [inventory, lookups]
  );

  const locationFormOptions = useMemo(
    () => (Array.isArray(lookups?.location) && lookups.location.length ? lookups.location : uniqueValuesByNormalized(inventory, 'location')),
    [inventory, lookups]
  );

	  const warrantyTypeOptions = useMemo(
	    () => (Array.isArray(lookups?.warranty_type) && lookups.warranty_type.length ? lookups.warranty_type : ['ADP', 'Without ADP']),
	    [lookups]
	  );

  const deallocationRemarkOptions = useMemo(
    () => (Array.isArray(lookups?.dealloc_remark) && lookups.dealloc_remark.length ? lookups.dealloc_remark : []),
    [lookups]
  );

  const deactivationReasonOptions = useMemo(
    () => (Array.isArray(lookups?.deactivation_reason) && lookups.deactivation_reason.length ? lookups.deactivation_reason : []),
    [lookups]
  );

  const [showAdd, setShowAdd] = useState(false);
					  const [addForm, setAddForm] = useState({
				    assetCode: '',
				    serialNumber: '',
			    manufacture: '',
			    modelDetails: '',
			    assetType: '',
			    location: '',
		    // Status is derived/managed via allocation/deallocation/deactivation flows.
			    // Keep a safe default for new assets created from UI.
				    status: 'In Store',
				    ipAddress: '',
			    osDetails: '',
		    poDetails: '',
	    sapAssetCode: '',
	    purchaseDetails: '',
	    invoiceDate: '',
	    warrantyType: '',
	    warrantyStartDate: '',
	    warrantyEndDate: '',
	    ramSizeGb: '',
	    cpu: '',
	    employeeType: '',
    remark: '',
	    lastDeallocationDate: '',
	    deactivationDate: '',
    allocatedUserId: '',
    allocEmpId: '',
    allocUserName: '',
    allocEmail: '',
    requestId: '',
    allocationRemark: '',
	    allocatedDate: '',
	    allocatedEndDate: OPEN_END_DATE,
	    allocLastDeallocationDate: '',
	    allocDeactivationDate: ''
	  });

  const [activeAsset, setActiveAsset] = useState(null);
  const [showEditMaster, setShowEditMaster] = useState(false);
  const [editMasterForm, setEditMasterForm] = useState(null);
  const [showAllocate, setShowAllocate] = useState(false);
  const [showDeallocate, setShowDeallocate] = useState(false);
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [deletingAssetId, setDeletingAssetId] = useState(null);

	  const [allocateForm, setAllocateForm] = useState({
	    empId: '',
	    userName: '',
	    email: '',
	    allocatedDate: new Date().toISOString().slice(0, 10),
	    requestId: '',
	    hostName: '',
	    remark: '',
	    lastDeallocationDate: '',
	    deactivationDate: ''
	  });

		  const [deallocateForm, setDeallocateForm] = useState({
		    deallocatedDate: new Date().toISOString().slice(0, 10),
		    requestId: '',
		    remark: '',
		    lastDeallocationDate: ''
		  });

		  const [deactivateForm, setDeactivateForm] = useState({
		    deactivationDate: new Date().toISOString().slice(0, 10),
        deactivationReason: '',
		  });

  const resetMessages = () => {
    setError('');
    setSuccess('');
  };

  const loadLookups = useCallback(async () => {
    try {
      const lk = await itAssetsAPI.getLookups('IT_ASSETS');
      // axios interceptor returns `response.data`, so lk is usually `{ success, data }`.
      // Keep backward-compatible handling if this ever returns a raw axios response.
      const resolved =
        (lk && typeof lk === 'object' && 'success' in lk && lk.data) ? lk.data
          : lk?.data?.data
            || lk?.data
            || {};
      setLookups(resolved);
    } catch {
      setLookups({});
    }
  }, []);

  const loadData = useCallback(async () => {
    resetMessages();
    setLoading(true);
    try {
      const my = await itAssetsAPI.getMyAssets();
      setMyAssets(Array.isArray(my?.data) ? my.data : my?.data?.data || []);

      await loadLookups();

      try {
        // Ensure IT_HEAD users see the management tabs even if the inventory API returns 403
        const allowManageByRole = isAdminRole || roleType === 'IT_HEAD';
        setCanManage(allowManageByRole);
        const inv = await itAssetsAPI.getInventory();
        setInventory(Array.isArray(inv?.data) ? inv.data : inv?.data?.data || []);
        // If inventory fetch succeeds, ensure canManage is true.
        setCanManage(true);
      } catch (e) {
        const status = e?.statusCode ?? e?.response?.status;
        if (status === 403) {
          // If role permits management (IT_HEAD), keep tabs visible; otherwise hide.
          if (!(isAdminRole || roleType === 'IT_HEAD')) {
            setCanManage(false);
          }
          setInventory([]);
        } else {
          throw e;
        }
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.data?.message || e?.message || 'Failed to load IT assets.');
    } finally {
      setLoading(false);
    }
  }, [loadLookups]);

  useEffect(() => {
    const onLookupsChanged = () => {
      loadLookups();
    };
    globalThis.addEventListener('lookupsChanged', onLookupsChanged);
    return () => globalThis.removeEventListener('lookupsChanged', onLookupsChanged);
  }, [loadLookups]);

  const loadMyRequests = useCallback(async () => {
    if (!canRequest) return;
    setMyRequestsLoading(true);
    try {
      const res = await itAssetsAPI.listMyRequests();
      const rows = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
      setMyRequestRows(rows);
    } catch (e) {
      setError(e?.message || 'Failed to load your requests.');
    } finally {
      setMyRequestsLoading(false);
    }
  }, [canRequest]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    // On the very first settle pass, a tab already present in the URL (from a
    // refresh, or Back/Forward landing here) wins over the Navbar-driven
    // initialTab prop — otherwise refreshing on a non-default tab would bounce
    // straight back to it.
    if (!hasSettledTabRef.current) {
      const urlTab = searchParams.get('tab');
      if (urlTab) {
        setActiveTabState(!canManage ? 'my' : urlTab);
        if (canManage) hasSettledTabRef.current = true;
        return;
      }
    }
    const desired = String(initialTab || 'my');
    setActiveTab(!canManage ? 'my' : desired, { replace: !hasSettledTabRef.current });
    if (canManage) hasSettledTabRef.current = true;
  }, [initialTab, canManage]);

  const onCreateAsset = async () => {
    resetMessages();
    try {
      const serialNumber = String(addForm.serialNumber || '').trim();
      if (!serialNumber) {
        setError('Serial Number is required.');
        return;
      }
      // SR Number is only meaningful for the EDITOR "submit for approval" path
      // (it's the ticket reference IT Head reviews against) — the backend's
      // direct createAsset endpoint (ADMIN) never requires or even uses it,
      // so it shouldn't block a direct add.
      if (!canApprove && !String(addForm.requestId || '').trim()) {
        setError('SR Number is required.');
        return;
      }
      await loadLookups();
      const {
        allocatedUserId,
        allocEmpId,
        allocUserName,
        allocEmail,
        allocationRemark,
        allocatedDate,
        allocatedEndDate,
        allocLastDeallocationDate,
        allocDeactivationDate,
        lastDeallocationDate,
        deactivationDate,
        employeeType,
        ...masterForm
      } = addForm;
      const payload = { ...masterForm, serialNumber, status: 'In Store' };
      if (canApprove) {
        await itAssetsAPI.createAsset(payload);
        setSuccess('Asset added successfully.');
      } else if (canRequest) {
        await itAssetsAPI.createRequest({
          requestType: 'CREATE_ASSET',
          serialNumber,
          payload,
        });
        setSuccess('Submitted for IT Head approval.');
      } else {
        throw new Error('Access denied.');
      }
      setShowAdd(false);
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to add asset.');
    }
  };

  const openImportExcel = () => {
    setImportExcelFile(null);
    setImportExcelResult(null);
    setImportExcelError('');
    setShowImportExcel(true);
  };

  const onImportExcelFileChange = (e) => {
    setImportExcelFile(e.target.files?.[0] || null);
    setImportExcelResult(null);
    setImportExcelError('');
  };

  const onSubmitImportExcel = async () => {
    if (!importExcelFile) {
      setImportExcelError('Please choose an Excel (.xlsx/.xls) file to import.');
      return;
    }
    setImportExcelSubmitting(true);
    setImportExcelError('');
    setImportExcelResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importExcelFile);
      const res = await itAssetsAPI.importExcel(formData);
      setImportExcelResult(res?.data || null);
      await loadData();
    } catch (e) {
      setImportExcelError(e?.response?.data?.message || e?.message || 'Failed to import Excel file.');
    } finally {
      setImportExcelSubmitting(false);
    }
  };

  const openAllocate = (asset) => {
    setActiveAsset(asset);
    setAllocateForm((prev) => ({
      ...prev,
      requestId: '',
      hostName: '',
      allocatedDate: new Date().toISOString().slice(0, 10)
    }));
    setShowAllocate(true);
  };

  const openDeallocate = (asset) => {
    setActiveAsset(asset);
    setDeallocateForm((prev) => ({
      ...prev,
      requestId: '',
      deallocatedDate: new Date().toISOString().slice(0, 10)
    }));
    setShowDeallocate(true);
  };

		  const openDeactivate = (asset) => {
		    setActiveAsset(asset);
		    setDeactivateForm({
		      deactivationDate: new Date().toISOString().slice(0, 10),
		    });
		    setShowDeactivate(true);
		  };

  const openDelete = (asset) => {
    setDeletingAssetId(asset?.serial_number || null);
  };

  const openLocationChange = (asset) => {
    if (!asset?.serial_number) return;
    setActiveAsset(asset);
    setLocationChangeForm({ serialNumber: asset.serial_number, location: asset.location || '' });
    setShowLocationChange(true);
  };

  const openEditMaster = (asset) => {
    if (!asset?.serial_number) return;
    setEditMasterForm({
      serialNumber: asset.serial_number,
      newSerialNumber: asset.serial_number,
      assetCode: asset.genie_asset_code || asset.asset_code || '',
      manufacture: asset.manufacture || '',
      modelDetails: asset.model_details || '',
      assetType: asset.asset_type || '',
      location: asset.location || '',
      hostName: asset.host_name || '',
      ipAddress: asset.ip_address || '',
      osDetails: asset.os_details || '',
      poDetails: asset.po_details || '',
      sapAssetCode: asset.sap_asset_code || '',
      purchaseDetails: asset.purchase_details || '',
      invoiceDate: asset.invoice_date || '',
      warrantyType: asset.warranty_type || '',
      warrantyStartDate: asset.warranty_start_date || '',
      warrantyEndDate: asset.warranty_end_date || '',
      ramSizeGb: asset.ram_size_gb == null ? '' : String(asset.ram_size_gb),
      cpu: asset.cpu || '',
      remark: asset.remark || '',
    });
    setShowEditMaster(true);
  };

  const [showLocationChange, setShowLocationChange] = useState(false);
  const [locationChangeForm, setLocationChangeForm] = useState({ serialNumber: '', location: '' });

  const [showSetCondition, setShowSetCondition] = useState(false);
  const [conditionForm, setConditionForm] = useState({ serialNumber: '', assetCondition: 'Working', issues: [] });

  const openSetCondition = (asset) => {
    if (!asset?.serial_number) return;
    const existingIssues = String(asset.condition_issues || '')
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    setConditionForm({
      serialNumber: asset.serial_number,
      assetCondition: String(asset.asset_condition || '').trim().toLowerCase() === 'not working' ? 'Not Working' : 'Working',
      issues: existingIssues
    });
    setShowSetCondition(true);
  };

  const toggleConditionIssue = (issue) => {
    setConditionForm((prev) => {
      const has = prev.issues.includes(issue);
      return {
        ...prev,
        issues: has ? prev.issues.filter((v) => v !== issue) : [...prev.issues, issue]
      };
    });
  };

  const submitSetCondition = async () => {
    resetMessages();
    try {
      const serialNumber = String(conditionForm?.serialNumber || '').trim();
      if (!serialNumber) throw new Error('Serial Number is required.');
      if (conditionForm.assetCondition === 'Not Working' && !conditionForm.issues.length) {
        throw new Error('Select at least one faulty component.');
      }
      const payload = {
        assetCondition: conditionForm.assetCondition,
        issues: conditionForm.assetCondition === 'Not Working' ? conditionForm.issues : []
      };
      // Applies immediately — no IT_HEAD approval step for condition changes.
      await itAssetsAPI.updateAssetCondition(serialNumber, payload);
      setSuccess('Asset condition updated.');
      setShowSetCondition(false);
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to update asset condition.');
    }
  };

  const submitLocationChange = async () => {
    resetMessages();
    try {
      const serialNumber = String(locationChangeForm?.serialNumber || '').trim();
      if (!serialNumber) throw new Error('Serial Number is required.');
      const payload = { location: String(locationChangeForm?.location || '').trim() };
      if (isAdminRole) {
        await itAssetsAPI.updateAssetMaster(serialNumber, payload);
        setSuccess('Asset master updated.');
      } else if (canRequest) {
        await itAssetsAPI.createRequest({ requestType: 'LOCATION_CHANGE', serialNumber, payload });
        setSuccess('Submitted for IT Head approval.');
      } else {
        throw new Error('Access denied.');
      }
      setShowLocationChange(false);
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to change location.');
    }
  };

  const submitEditMaster = async () => {
    resetMessages();
    try {
      if (!editMasterForm?.serialNumber) throw new Error('Serial Number is required.');
      const trimmedNewSerial = String(editMasterForm?.newSerialNumber || '').trim();
      if (!trimmedNewSerial) throw new Error('Serial Number cannot be blank.');
      const isRenamingSerial = trimmedNewSerial !== editMasterForm.serialNumber;
      if (isRenamingSerial) {
        const ok = window.confirm(
          `Change Serial Number from "${editMasterForm.serialNumber}" to "${trimmedNewSerial}"?\n\n` +
          'This updates every allocation history record, condition issue, attachment, audit entry, and pending request for this asset to the new serial number. This cannot be undone automatically.'
        );
        if (!ok) return;
      }
      if (canApprove) {
        const res = await itAssetsAPI.updateAssetMaster(editMasterForm.serialNumber, {
          ...editMasterForm,
          newSerialNumber: trimmedNewSerial
        });
        setSuccess(res?.data?.warning ? `Asset master updated, but: ${res.data.warning}` : 'Asset master updated.');
      } else if (canRequest) {
        // Editors submit a LOCATION_CHANGE request for IT Head approval.
        await itAssetsAPI.createRequest({
          requestType: 'LOCATION_CHANGE',
          serialNumber: editMasterForm.serialNumber,
          payload: { location: editMasterForm.location }
        });
        setSuccess('Submitted for IT Head approval.');
      } else {
        throw new Error('Access denied.');
      }
      setShowEditMaster(false);
      setEditMasterForm(null);
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to update asset master.');
    }
  };

  const confirmDelete = async () => {
    resetMessages();
    try {
      if (!isAdminRole) throw new Error('Access denied.');
      await itAssetsAPI.deleteAsset(deletingAssetId);
      setSuccess('Asset deleted.');
      setDeletingAssetId(null);
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Delete failed.');
    }
  };

  const openInventoryAttachments = useCallback(async (row) => {
    const assetId = String(row?.serial_number || '').trim();
    if (!assetId) return;

    setShowInventoryAttachments(true);
    setInventoryAttachments([]);
    setInventoryAttachmentsError('');
    setInventorySelectedFile(null);
    setInventoryAttachmentAssetId(assetId);
    setInventoryAttachmentAllocationId(row?.allocation_id ?? null);
    setInventoryAttachmentsLoading(true);
    try {
      const res = await itAssetsAPI.listAttachments(assetId);
      setInventoryAttachments(Array.isArray(res?.data) ? res.data : res?.data?.data || []);
    } catch (e) {
      setInventoryAttachmentsError(e?.response?.data?.message || e?.message || 'Failed to load attachments.');
    } finally {
      setInventoryAttachmentsLoading(false);
    }
  }, []);

  const filteredInventoryAttachments = useMemo(() => {
    const allocId = inventoryAttachmentAllocationId;
    if (!allocId) return inventoryAttachments || [];
    return (inventoryAttachments || []).filter((a) => Number(a?.allocation_id) === Number(allocId));
  }, [inventoryAttachments, inventoryAttachmentAllocationId]);

  const onUploadInventoryAttachment = async () => {
    const assetId = String(inventoryAttachmentAssetId || '').trim();
    if (!assetId) {
      setInventoryAttachmentsError('Serial Number is required.');
      return;
    }
    if (!inventorySelectedFile) {
      setInventoryAttachmentsError('Please choose a file (PDF/PNG/JPEG) to upload.');
      return;
    }
    setInventoryUploading(true);
    setInventoryAttachmentsError('');
    try {
      const form = new FormData();
      form.append('file', inventorySelectedFile);
      if (inventoryAttachmentAllocationId) form.append('allocationId', String(inventoryAttachmentAllocationId));
      await itAssetsAPI.uploadAttachment(assetId, form);

      const res = await itAssetsAPI.listAttachments(assetId);
      setInventoryAttachments(Array.isArray(res?.data) ? res.data : res?.data?.data || []);
      setInventorySelectedFile(null);
      await loadData(); // refresh attachment_count in table
    } catch (e) {
      setInventoryAttachmentsError(e?.response?.data?.message || e?.message || 'Failed to upload attachment.');
    } finally {
      setInventoryUploading(false);
    }
  };

  const onDownloadInventoryAttachment = async (attachment) => {
    try {
      const res = await itAssetsAPI.downloadAttachment(attachment.id);
      const blob = res instanceof Blob ? res : (res?.data instanceof Blob ? res.data : new Blob([res?.data || '']));
      downloadBlobLocal(blob, attachment.original_name || `attachment-${attachment.id}`);
    } catch (e) {
      setInventoryAttachmentsError(e?.response?.data?.message || e?.message || 'Failed to download attachment.');
    }
  };

  const onDeleteInventoryAttachment = async (attachment) => {
    if (!isAdminRole) return;
    const ok = globalThis.confirm(`Delete attachment "${attachment.original_name || attachment.id}"?`);
    if (!ok) return;
    setInventoryAttachmentsError('');
    try {
      await itAssetsAPI.deleteAttachment(attachment.id);
      const res = await itAssetsAPI.listAttachments(String(inventoryAttachmentAssetId || '').trim());
      setInventoryAttachments(Array.isArray(res?.data) ? res.data : res?.data?.data || []);
      await loadData();
    } catch (e) {
      setInventoryAttachmentsError(e?.response?.data?.message || e?.message || 'Failed to delete attachment.');
    }
  };

  const closeInventoryAttachmentPreview = useCallback(() => {
    setShowInventoryAttachmentPreview(false);
    if (inventoryPreviewUrl) URL.revokeObjectURL(inventoryPreviewUrl);
    setInventoryPreviewUrl('');
    setInventoryPreviewName('');
    setInventoryPreviewMime('');
    setInventoryPreviewLoading(false);
    setInventoryPreviewScale(1);
    if (inventoryPreviewReturnToList) setShowInventoryAttachments(true);
    setInventoryPreviewReturnToList(false);
  }, [inventoryPreviewUrl]);

  const onViewInventoryAttachment = async (attachment) => {
    setInventoryPreviewLoading(true);
    setInventoryAttachmentsError('');
    try {
      const res = await itAssetsAPI.downloadAttachment(attachment.id);
      const fallbackMime = attachment?.mime_type || inferMimeFromName(attachment?.original_name);
      const rawBlob = res instanceof Blob ? res : (res?.data instanceof Blob ? res.data : new Blob([res?.data || ''], { type: fallbackMime || 'application/octet-stream' }));
      const blob = ensureBlobType(rawBlob, fallbackMime);
      const url = URL.createObjectURL(blob);
      if (inventoryPreviewUrl) URL.revokeObjectURL(inventoryPreviewUrl);
      setInventoryPreviewUrl(url);
      setInventoryPreviewName(attachment.original_name || `attachment-${attachment.id}`);
      setInventoryPreviewMime(String(fallbackMime || blob.type || ''));
      // Avoid stacking modals (can cause blank screen/backdrop issues).
      setInventoryPreviewReturnToList(true);
      setShowInventoryAttachments(false);
      setShowInventoryAttachmentPreview(true);
    } catch (e) {
      setInventoryAttachmentsError(e?.response?.data?.message || e?.message || 'Failed to view attachment.');
    } finally {
      setInventoryPreviewLoading(false);
    }
  };

  const submitAllocate = async () => {
    resetMessages();
    try {
      const payload = { ...allocateForm };
      if (!String(payload.requestId || '').trim()) {
        setError('SR Number is required.');
        return;
      }
      if (canApprove) {
        await itAssetsAPI.allocateAsset(activeAsset.serial_number, payload);
        setSuccess('Asset allocated.');
      } else if (canRequest) {
        await itAssetsAPI.createRequest({ requestType: 'ALLOCATE', serialNumber: activeAsset.serial_number, payload });
        setSuccess('Allocation request submitted for approval.');
      } else {
        throw new Error('Access denied.');
      }
      setShowAllocate(false);
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Allocation failed.');
    }
  };

  const submitDeallocate = async () => {
    resetMessages();
    try {
      if (!String(deallocateForm.requestId || '').trim()) {
        setError('SR Number is required.');
        return;
      }
      if (canApprove) {
        await itAssetsAPI.deallocateAsset(activeAsset.serial_number, deallocateForm);
        setSuccess('Asset deallocated.');
      } else if (canRequest) {
        await itAssetsAPI.createRequest({ requestType: 'DEALLOCATE', serialNumber: activeAsset.serial_number, payload: deallocateForm });
        setSuccess('Deallocation request submitted for approval.');
      } else {
        throw new Error('Access denied.');
      }
      setShowDeallocate(false);
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Deallocation failed.');
    }
  };

	  const submitDeactivate = async () => {
	    resetMessages();
	    try {
	      if (!canApprove) throw new Error('Access denied.');
        if (!String(deactivateForm.deactivationReason || '').trim()) {
          setError('Deactivation Reason is required.');
          return;
        }
	      const payload = {
	        ...deactivateForm,
	        lastDeallocationDate: deactivateForm.deactivationDate
	      };
      await itAssetsAPI.deactivateAsset(activeAsset.serial_number, payload);
      setSuccess('Asset deactivated.');
      setShowDeactivate(false);
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Deactivate failed.');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="align-items-center mb-3">
        <Col>
          <h2 className="mb-1">IT Asset Management</h2>
        </Col>
        <Col className="text-end">
          <Button variant="outline-secondary" className="me-2" onClick={loadData}>
            <FiRefreshCw className="me-1" /> Refresh
          </Button>
          {canApprove && (
            <Button
              variant="outline-primary"
              className="me-2"
              onClick={() => navigate('/it-assets/approvals')}
            >
              Approvals
            </Button>
          )}
          {canRequest && (
            <Button
              variant="outline-primary"
              className="me-2"
              onClick={async () => {
                await loadMyRequests();
                setShowMyRequests(true);
              }}
            >
              My Requests
            </Button>
          )}
          {canApprove && (
            <Button variant="outline-primary" className="me-2" onClick={openImportExcel}>
              <FiUpload className="me-1" /> Import Excel
            </Button>
          )}
          {canManage && (
            <Button
              variant="primary"
              onClick={async () => {
                await loadLookups();
                setShowAdd(true);
              }}
            >
              <FiPlus className="me-1" /> Add Asset
            </Button>
          )}
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <div className="mb-3">
        {activeTab === 'dashboard' && canManage && <ITAssetDashboardPanel canManage={canManage} />}
        {activeTab === 'my' && <MyAssetsPanel rows={myAssets} lookups={lookups} onAssetIdClick={openAssetHistory} />}
        {activeTab === 'master' && canManage && (
          <DataTable
            rows={inventory}
            columns={MASTER_COLUMNS}
            lookups={lookups}
            canManage={false}
            canEditMaster={canApprove}
            onEditMaster={openEditMaster}
            canDelete={isAdminRole}
            onDelete={openDelete}
            showAllocationFilter={false}
            showBlankStatusForDeactivated
            exportName="it-assets-master"
            onAssetIdClick={openAssetHistory}
          />
        )}
        {activeTab === 'inventory' && canManage && (
          <DataTable
            rows={inventory.filter((row) => String(row?.status || '').trim().toLowerCase() !== 'deactivated')}
            columns={ACTIVE_ASSET_COLUMNS}
            lookups={lookups}
            onAssetIdClick={openAssetHistory}
              onUserClick={openUserHistory}
            onAllocate={openAllocate}
            onDeallocate={openDeallocate}
            onDeactivate={openDeactivate}
            onLocationChange={openLocationChange}
            onSetCondition={openSetCondition}
            onOpenAttachments={openInventoryAttachments}
            canManage={canManage}
            showAllocationFilter
            showAttachmentColumn
            exportName="it-assets-current-allocation"
          />
        )}
        {activeTab === 'history' && canManage && (
          <AssetHistoryPanel
            canManage={canManage}
            presetAssetId={historyPresetAssetId}
            onPresetConsumed={consumeHistoryPreset}
          />
        )}
        {activeTab === 'user_history' && canManage && (
          <UserHistoryPanel
            canManage={canManage}
            onAssetIdClick={openAssetHistory}
            presetQ={userHistoryPresetQ}
            onPresetConsumed={consumeUserHistoryPreset}
          />
        )}
        {activeTab === 'audit' && canManage && (
          <AssetAuditPanel canManage={canManage} onAssetIdClick={openAssetHistory} />
        )}
      </div>

      {/* Delete asset confirm */}
      <Modal show={Boolean(deletingAssetId)} onHide={() => setDeletingAssetId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Asset</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This will permanently remove asset <span className="fw-semibold">{deletingAssetId}</span> from Master and Allocation history.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeletingAssetId(null)}>Cancel</Button>
          <Button variant="danger" onClick={confirmDelete}>Delete</Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Asset Master (Admin) */}
      <Modal show={showEditMaster} onHide={() => setShowEditMaster(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Asset Master</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editMasterForm?.serialNumber ? (
            canApprove ? (
              <Row className="g-3 mb-1">
                <Col md={4}>
                  <Form.Label>Serial Number</Form.Label>
                  <Form.Control
                    value={editMasterForm?.newSerialNumber ?? ''}
                    onChange={(e) => setEditMasterForm((p) => ({ ...p, newSerialNumber: e.target.value }))}
                  />
                  {editMasterForm.newSerialNumber?.trim() && editMasterForm.newSerialNumber.trim() !== editMasterForm.serialNumber && (
                    <div className="text-warning small mt-1">
                      Changing this updates allocation history, condition issues, attachments, audits, and pending requests to the new serial number.
                    </div>
                  )}
                </Col>
              </Row>
            ) : (
              <div className="text-muted small mb-3">
                Serial Number: <span className="fw-semibold">{editMasterForm.serialNumber}</span>
              </div>
            )
          ) : null}
          <Row className="g-3">
            <Col md={4}>
              <Form.Label>Genie Asset Code</Form.Label>
              <Form.Control value={editMasterForm?.assetCode || ''} onChange={(e) => setEditMasterForm((p) => ({ ...p, assetCode: e.target.value }))} />
            </Col>
            <Col md={4}>
              <Form.Label>Manufacture(Make)</Form.Label>
              <Form.Control value={editMasterForm?.manufacture || ''} onChange={(e) => setEditMasterForm((p) => ({ ...p, manufacture: e.target.value }))} />
            </Col>
            <Col md={4}>
              <Form.Label>Model Details</Form.Label>
              <Form.Control value={editMasterForm?.modelDetails || ''} onChange={(e) => setEditMasterForm((p) => ({ ...p, modelDetails: e.target.value }))} />
            </Col>

            <Col md={4}>
              <Form.Label>Asset Type</Form.Label>
              <Form.Select value={editMasterForm?.assetType || ''} onChange={(e) => setEditMasterForm((p) => ({ ...p, assetType: e.target.value }))}>
                <option value="">Select</option>
                {assetTypeOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>Location</Form.Label>
              <Form.Select value={editMasterForm?.location || ''} onChange={(e) => setEditMasterForm((p) => ({ ...p, location: e.target.value }))}>
                <option value="">Select</option>
                {locationFormOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </Form.Select>
            </Col>

            <Col md={4}>
              <Form.Label>(Host-Name)</Form.Label>
              <Form.Control value={editMasterForm?.hostName || ''} onChange={(e) => setEditMasterForm((p) => ({ ...p, hostName: e.target.value }))} />
            </Col>
            <Col md={4}>
              <Form.Label>IP Address</Form.Label>
              <Form.Control value={editMasterForm?.ipAddress || ''} onChange={(e) => setEditMasterForm((p) => ({ ...p, ipAddress: e.target.value }))} />
            </Col>
            <Col md={4}>
              <Form.Label>OS Details</Form.Label>
              <Form.Select value={editMasterForm?.osDetails || ''} onChange={(e) => setEditMasterForm((p) => ({ ...p, osDetails: e.target.value }))}>
                <option value="">Select</option>
                {osDetailsOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </Form.Select>
            </Col>

            <Col md={4}>
              <Form.Label>PO Details</Form.Label>
              <Form.Control value={editMasterForm?.poDetails || ''} onChange={(e) => setEditMasterForm((p) => ({ ...p, poDetails: e.target.value }))} />
            </Col>
            <Col md={4}>
              <Form.Label>SAP Asset Code</Form.Label>
              <Form.Control value={editMasterForm?.sapAssetCode || ''} onChange={(e) => setEditMasterForm((p) => ({ ...p, sapAssetCode: e.target.value }))} />
            </Col>
            <Col md={4}>
              <Form.Label>Purchase Details</Form.Label>
              <Form.Control
                value={editMasterForm?.purchaseDetails || ''}
                onChange={(e) => setEditMasterForm((p) => ({ ...p, purchaseDetails: e.target.value }))}
                placeholder="e.g. BI/8965/20-21"
              />
            </Col>

            <Col md={4}>
              <Form.Label>Invoice Date</Form.Label>
              <Form.Control type="date" value={editMasterForm?.invoiceDate || ''} onChange={(e) => setEditMasterForm((p) => ({ ...p, invoiceDate: e.target.value }))} />
            </Col>
            <Col md={4}>
              <Form.Label>Warranty Type</Form.Label>
              <Form.Select value={editMasterForm?.warrantyType || ''} onChange={(e) => setEditMasterForm((p) => ({ ...p, warrantyType: e.target.value }))}>
                <option value="">Select</option>
                {warrantyTypeOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>Warranty Start DT</Form.Label>
              <Form.Control type="date" value={editMasterForm?.warrantyStartDate || ''} onChange={(e) => setEditMasterForm((p) => ({ ...p, warrantyStartDate: e.target.value }))} />
            </Col>
            <Col md={4}>
              <Form.Label>Warranty End DT</Form.Label>
              <Form.Control type="date" value={editMasterForm?.warrantyEndDate || ''} onChange={(e) => setEditMasterForm((p) => ({ ...p, warrantyEndDate: e.target.value }))} />
            </Col>
            <Col md={4}>
              <Form.Label>RAM Size(GB)</Form.Label>
              <Form.Select value={editMasterForm?.ramSizeGb || ''} onChange={(e) => setEditMasterForm((p) => ({ ...p, ramSizeGb: e.target.value }))}>
                <option value="">Select</option>
                {ramSizeOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>CPU</Form.Label>
              <Form.Select value={editMasterForm?.cpu || ''} onChange={(e) => setEditMasterForm((p) => ({ ...p, cpu: e.target.value }))}>
                <option value="">Select</option>
                {cpuOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={12}>
              <Form.Label>Remark</Form.Label>
              <Form.Control as="textarea" rows={3} value={editMasterForm?.remark || ''} onChange={(e) => setEditMasterForm((p) => ({ ...p, remark: e.target.value }))} />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditMaster(false)}>Cancel</Button>
          <Button
            variant="primary"
            onClick={submitEditMaster}
            disabled={!canApprove || !editMasterForm?.serialNumber || !String(editMasterForm?.newSerialNumber || '').trim()}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Location Change modal (Admin direct update or Editor request) */}
      <Modal show={showLocationChange} onHide={() => setShowLocationChange(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Change Asset Location</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col>
              <Form.Label>Location</Form.Label>
              <Form.Select value={locationChangeForm.location || ''} onChange={(e) => setLocationChangeForm((p) => ({ ...p, location: e.target.value }))}>
                <option value="">Select</option>
                {locationFormOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLocationChange(false)}>Cancel</Button>
          <Button variant="primary" onClick={submitLocationChange}>Save</Button>
        </Modal.Footer>
      </Modal>

      {/* Set Asset Condition (Active Asset tab only) */}
      <Modal show={showSetCondition} onHide={() => setShowSetCondition(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Set Asset Condition</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted small mb-3">
            Serial Number: <span className="fw-semibold">{conditionForm.serialNumber || '-'}</span>
          </div>
          <Form.Group className="mb-3">
            <Form.Label>Condition</Form.Label>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio"
                id="condition-working"
                label="Working"
                name="asset-condition"
                checked={conditionForm.assetCondition === 'Working'}
                onChange={() => setConditionForm((p) => ({ ...p, assetCondition: 'Working' }))}
              />
              <Form.Check
                type="radio"
                id="condition-not-working"
                label="Not Working"
                name="asset-condition"
                checked={conditionForm.assetCondition === 'Not Working'}
                onChange={() => setConditionForm((p) => ({ ...p, assetCondition: 'Not Working' }))}
              />
            </div>
          </Form.Group>
          {conditionForm.assetCondition === 'Not Working' && (
            <Form.Group>
              <Form.Label>What's not working?</Form.Label>
              <Row className="g-2">
                {(Array.isArray(lookups?.faulty) && lookups.faulty.length ? lookups.faulty : []).map((option) => (
                  <Col xs={6} key={option}>
                    <Form.Check
                      type="checkbox"
                      id={`condition-issue-${option}`}
                      label={option}
                      checked={conditionForm.issues.includes(option)}
                      onChange={() => toggleConditionIssue(option)}
                    />
                  </Col>
                ))}
              </Row>
              {!(Array.isArray(lookups?.faulty) && lookups.faulty.length) && (
                <div className="text-muted small mt-2">
                  No component list configured yet. Add options under Admin → Master Tables ("faulty").
                </div>
              )}
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSetCondition(false)}>Cancel</Button>
          <Button variant="primary" onClick={submitSetCondition}>Save</Button>
        </Modal.Footer>
      </Modal>

      {/* Current Allocation attachments modal */}
      <Modal show={showInventoryAttachments} onHide={() => setShowInventoryAttachments(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Attachments</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {inventoryAttachmentsError && (
            <Alert variant="danger" dismissible onClose={() => setInventoryAttachmentsError('')}>
              {inventoryAttachmentsError}
            </Alert>
          )}

	          <div className="text-muted small mb-2">
	            Asset: <span className="fw-semibold">{inventoryAttachmentAssetId || '-'}</span>
	          </div>

          <Form.Control
            type="file"
            accept="image/png,image/jpeg,application/pdf"
            onChange={(e) => setInventorySelectedFile(e.target.files?.[0] || null)}
          />
          <div className="d-flex justify-content-end mt-2">
            <Button variant="primary" onClick={onUploadInventoryAttachment} disabled={inventoryUploading || !inventorySelectedFile}>
              {inventoryUploading ? 'Uploading…' : 'Upload'}
            </Button>
          </div>

          <div className="mt-3">
            {inventoryAttachmentsLoading ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" /> Loading…
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover size="sm" className="align-middle mb-0 small">
                  <thead className="table-light">
	                    <tr>
	                      <th className="text-nowrap">Uploaded At</th>
	                      <th className="text-nowrap">File</th>
	                      <th className="text-nowrap">Action</th>
	                    </tr>
	                  </thead>
	                  <tbody>
	                    {filteredInventoryAttachments.map((a) => (
	                      <tr key={a.id}>
	                        <td className="text-nowrap">{formatDate(a.uploaded_at)}</td>
	                        <td className="text-nowrap">{a.original_name || `attachment-${a.id}`}</td>
	                        <td className="text-nowrap">
	                          <div className="d-flex gap-2">
	                            <Button size="sm" variant="outline-primary" onClick={() => onViewInventoryAttachment(a)} disabled={inventoryPreviewLoading}>
	                              View
                            </Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => onDownloadInventoryAttachment(a)}>
                              Download
                            </Button>
                            {isAdminRole ? (
                              <Button size="sm" variant="outline-danger" onClick={() => onDeleteInventoryAttachment(a)}>
                                Delete
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
	                    {!filteredInventoryAttachments.length && (
	                      <tr>
	                        <td colSpan={3} className="text-center text-muted py-3">
	                          No attachments found.
	                        </td>
	                      </tr>
	                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>

      {/* Current Allocation attachment preview modal */}
      <Modal show={showInventoryAttachmentPreview} onHide={closeInventoryAttachmentPreview} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{inventoryPreviewName || 'Preview'}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: 520 }}>
          {inventoryPreviewLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="sm" /> Loading…
            </div>
          ) : !inventoryPreviewUrl ? (
            <div className="text-center text-muted py-5">No preview available.</div>
            ) : String(inventoryPreviewMime || '').toLowerCase().startsWith('image/') ? (
              <div style={{ overflow: 'auto', display: 'flex', justifyContent: 'center' }}>
                <img
                  src={inventoryPreviewUrl}
                  alt={inventoryPreviewName || 'Attachment'}
                  style={{
                    transform: `scale(${inventoryPreviewScale})`,
                    transformOrigin: 'center top',
                    maxWidth: 'none',
                    maxHeight: `${70 * inventoryPreviewScale}vh`
                  }}
                />
              </div>
            ) : (
              <div style={{ overflow: 'auto' }}>
                <div style={{ transform: `scale(${inventoryPreviewScale})`, transformOrigin: 'center top' }}>
                  <iframe title="Attachment Preview" src={inventoryPreviewUrl} style={{ width: '100%', height: '70vh', border: 0 }} />
                </div>
              </div>
          )}
        </Modal.Body>
          <Modal.Footer>
            <div className="me-auto d-flex align-items-center gap-2">
              <Button size="sm" variant="outline-secondary" onClick={() => setInventoryPreviewScale((s) => Math.max(0.25, +(s - 0.25).toFixed(2)))}>-</Button>
              <div className="small text-muted">Zoom: {Math.round(inventoryPreviewScale * 100)}%</div>
              <Button size="sm" variant="outline-secondary" onClick={() => setInventoryPreviewScale(1)}>Reset</Button>
              <Button size="sm" variant="outline-secondary" onClick={() => setInventoryPreviewScale((s) => Math.min(3, +(s + 0.25).toFixed(2)))}>+</Button>
            </div>
            <Button variant="secondary" onClick={closeInventoryAttachmentPreview}>Close</Button>
          </Modal.Footer>
      </Modal>

      {/* Add asset modal */}
      <Modal show={showAdd} onHide={() => setShowAdd(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Asset</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {canApprove === false && canRequest && (assetTypeOptions.length === 0 || locationFormOptions.length === 0) && (
            <Alert variant="warning" className="mb-3">
              Dropdown master data not loaded yet. Try clicking Refresh on the page, or ensure options exist in Admin → Master Tables for
              {' '}
              <span className="fw-semibold">asset_type</span> and <span className="fw-semibold">location</span>.
            </Alert>
          )}
	          <Row className="g-3">
	            <Col md={4}>
	              <Form.Label>Genie Asset Code</Form.Label>
	              <Form.Control value={addForm.assetCode} onChange={(e) => setAddForm({ ...addForm, assetCode: e.target.value })} />
	            </Col>
	            <Col md={4}>
	              <Form.Label>Serial Number *</Form.Label>
	              <Form.Control
	                value={addForm.serialNumber}
	                onChange={(e) => setAddForm((prev) => ({ ...prev, serialNumber: e.target.value }))}
	              />
	            </Col>

            <Col md={4}>
              <Form.Label>Manufacture(Make)</Form.Label>
              <Form.Control value={addForm.manufacture} onChange={(e) => setAddForm({ ...addForm, manufacture: e.target.value })} />
            </Col>
            <Col md={4}>
              <Form.Label>Model Details</Form.Label>
              <Form.Control value={addForm.modelDetails} onChange={(e) => setAddForm({ ...addForm, modelDetails: e.target.value })} />
            </Col>
            <Col md={4}>
              <Form.Label>Asset Type</Form.Label>
              <Form.Select value={addForm.assetType} onChange={(e) => setAddForm({ ...addForm, assetType: e.target.value })}>
                <option value="">Select</option>
                {assetTypeOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </Form.Select>
            </Col>

            <Col md={4}>
              <Form.Label>Location</Form.Label>
              <Form.Select value={addForm.location} onChange={(e) => setAddForm({ ...addForm, location: e.target.value })}>
                <option value="">Select</option>
                {locationFormOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </Form.Select>
            </Col>
		            <Col md={4}>
		              <Form.Label>SR Number *</Form.Label>
		              <Form.Control value={addForm.requestId} onChange={(e) => setAddForm({ ...addForm, requestId: e.target.value })} />
		            </Col>
	            <Col md={4}>
	              <div />
	            </Col>

	            <Col md={4}>
	              <Form.Label>IP Address</Form.Label>
	              <Form.Control value={addForm.ipAddress} onChange={(e) => setAddForm({ ...addForm, ipAddress: e.target.value })} />
	            </Col>
            <Col md={4}>
              <Form.Label>OS Details</Form.Label>
              <Form.Select value={addForm.osDetails} onChange={(e) => setAddForm({ ...addForm, osDetails: e.target.value })}>
                <option value="">Select</option>
                {osDetailsOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </Form.Select>
            </Col>

            <Col md={4}>
              <Form.Label>PO Details</Form.Label>
              <Form.Control value={addForm.poDetails} onChange={(e) => setAddForm({ ...addForm, poDetails: e.target.value })} />
            </Col>
            <Col md={4}>
              <Form.Label>SAP Asset Code</Form.Label>
              <Form.Control value={addForm.sapAssetCode} onChange={(e) => setAddForm({ ...addForm, sapAssetCode: e.target.value })} />
            </Col>
            <Col md={4}>
              <Form.Label>Warranty Start DT</Form.Label>
              <Form.Control type="date" value={addForm.warrantyStartDate} onChange={(e) => setAddForm({ ...addForm, warrantyStartDate: e.target.value })} />
            </Col>
            <Col md={4}>
              <Form.Label>Warranty End DT</Form.Label>
              <Form.Control type="date" value={addForm.warrantyEndDate} onChange={(e) => setAddForm({ ...addForm, warrantyEndDate: e.target.value })} />
            </Col>
            <Col md={4}>
              <Form.Label>Purchase Details</Form.Label>
              <Form.Control
                value={addForm.purchaseDetails}
                onChange={(e) => setAddForm({ ...addForm, purchaseDetails: e.target.value })}
                placeholder="e.g. BI/8965/20-21"
              />
            </Col>
            <Col md={4}>
              <Form.Label>Invoice Date</Form.Label>
              <Form.Control type="date" value={addForm.invoiceDate} onChange={(e) => setAddForm({ ...addForm, invoiceDate: e.target.value })} />
            </Col>
            <Col md={4}>
              <Form.Label>Warranty Type</Form.Label>
              <Form.Select value={addForm.warrantyType} onChange={(e) => setAddForm({ ...addForm, warrantyType: e.target.value })}>
                <option value="">Select</option>
                {warrantyTypeOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>RAM Size(GB)</Form.Label>
              <Form.Select value={addForm.ramSizeGb} onChange={(e) => setAddForm({ ...addForm, ramSizeGb: e.target.value })}>
                <option value="">Select</option>
                {ramSizeOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </Form.Select>
            </Col>
	            <Col md={4}>
	              <Form.Label>CPU</Form.Label>
	              <Form.Select value={addForm.cpu} onChange={(e) => setAddForm({ ...addForm, cpu: e.target.value })}>
	                <option value="">Select</option>
	                {cpuOptions.map((value) => (
	                  <option key={value} value={value}>{value}</option>
	                ))}
	              </Form.Select>
	            </Col>

            <Col md={12}>
              <Form.Label>Remark</Form.Label>
              <Form.Control as="textarea" rows={3} value={addForm.remark} onChange={(e) => setAddForm({ ...addForm, remark: e.target.value })} />
            </Col>
          </Row>
        </Modal.Body>
	        <Modal.Footer>
	          <Button variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Button>
		          <Button
		            variant="primary"
		            onClick={onCreateAsset}
		            disabled={!String(addForm.serialNumber || '').trim() || (!canApprove && !String(addForm.requestId || '').trim())}
		          >
		            {canApprove ? 'Save' : 'Submit for Approval'}
		          </Button>
	        </Modal.Footer>
      </Modal>

      {/* Import Excel modal (ADMIN only) */}
      <Modal show={showImportExcel} onHide={() => setShowImportExcel(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Import IT Assets from Excel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted small mb-2">
            Upload a filled copy of the IT Asset import template (sheets: &quot;Overall Asset Master&quot;,
            &quot;Allocated Asset&quot;, &quot;In Store&quot;). To regenerate a blank template, run{' '}
            <code>node backend/scripts/generateItAssetsExcelTemplate.js</code>.
          </div>

          {importExcelError && (
            <Alert variant="danger" dismissible onClose={() => setImportExcelError('')}>
              {importExcelError}
            </Alert>
          )}

          {importExcelResult && (
            <Alert variant="success">
              Import completed — {importExcelResult.masterUpserts ?? 0} asset(s) upserted,{' '}
              {importExcelResult.allocationRows ?? 0} allocation row(s) recorded,{' '}
              {importExcelResult.skipped ?? 0} row(s) skipped.
              {Array.isArray(importExcelResult.selectedSheetNames) && importExcelResult.selectedSheetNames.length > 0 && (
                <div className="small text-muted mt-1">Sheets processed: {importExcelResult.selectedSheetNames.join(', ')}</div>
              )}
            </Alert>
          )}

          <Form.Control
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={onImportExcelFileChange}
            disabled={importExcelSubmitting}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImportExcel(false)}>Close</Button>
          <Button variant="primary" onClick={onSubmitImportExcel} disabled={importExcelSubmitting || !importExcelFile}>
            {importExcelSubmitting ? 'Importing…' : 'Import'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Allocate modal */}
      <Modal show={showAllocate} onHide={() => setShowAllocate(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Allocate Asset</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted small mb-2">
            Asset: <span className="fw-semibold">{activeAsset?.serial_number}</span>
          </div>
	          <Row className="g-3">
	            <Col md={6}>
	              <Form.Label>E-mail ID *</Form.Label>
	              <Form.Control value={allocateForm.email} onChange={(e) => setAllocateForm({ ...allocateForm, email: e.target.value })} />
	            </Col>
	            <Col md={6}>
	              <Form.Label>Allocated Date *</Form.Label>
	              <Form.Control type="date" value={allocateForm.allocatedDate} onChange={(e) => setAllocateForm({ ...allocateForm, allocatedDate: e.target.value })} />
	            </Col>
	            <Col md={6}>
	              <Form.Label>(Host-Name)</Form.Label>
	              <Form.Control value={allocateForm.hostName} onChange={(e) => setAllocateForm({ ...allocateForm, hostName: e.target.value })} />
	            </Col>
	            <Col md={6}>
	              <div />
	            </Col>
	            <Col md={6}>
	              <Form.Label>Emp ID</Form.Label>
	              <Form.Control value={allocateForm.empId} onChange={(e) => setAllocateForm({ ...allocateForm, empId: e.target.value })} />
	            </Col>
	            <Col md={6}>
              <Form.Label>User Name</Form.Label>
              <Form.Control value={allocateForm.userName} onChange={(e) => setAllocateForm({ ...allocateForm, userName: e.target.value })} />
            </Col>
	            <Col md={12}>
	              <Form.Label>SR Number *</Form.Label>
	              <Form.Control
	                value={allocateForm.requestId}
	                onChange={(e) => setAllocateForm({ ...allocateForm, requestId: e.target.value })}
	              />
	            </Col>
	          </Row>
        </Modal.Body>
	        <Modal.Footer>
	          <Button variant="secondary" onClick={() => setShowAllocate(false)}>Cancel</Button>
	          <Button
	            variant="primary"
	            onClick={submitAllocate}
	            disabled={!String(allocateForm.email || '').trim() || !String(allocateForm.requestId || '').trim()}
	          >
	            {canApprove ? 'Allocate' : 'Submit for Approval'}
	          </Button>
	        </Modal.Footer>
      </Modal>

      {/* Deallocate modal */}
      <Modal show={showDeallocate} onHide={() => setShowDeallocate(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Deallocate Asset</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted small mb-2">
            Asset: <span className="fw-semibold">{activeAsset?.serial_number}</span>
          </div>
	          <Row className="g-3">
	            <Col md={6}>
	              <Form.Label>Deallocated Date *</Form.Label>
	              <Form.Control type="date" value={deallocateForm.deallocatedDate} onChange={(e) => setDeallocateForm({ ...deallocateForm, deallocatedDate: e.target.value })} />
	            </Col>
	            <Col md={6}>
	              <Form.Label>SR Number *</Form.Label>
	              <Form.Control value={deallocateForm.requestId} onChange={(e) => setDeallocateForm({ ...deallocateForm, requestId: e.target.value })} />
	            </Col>
		            <Col md={6}>
		              <div />
		            </Col>
		            <Col md={6}>
		              <div />
		            </Col>
		            <Col md={12}>
		              <Form.Label>Deallocation Remarks</Form.Label>
		              <Form.Select value={deallocateForm.remark} onChange={(e) => setDeallocateForm({ ...deallocateForm, remark: e.target.value })}>
		                <option value="">Select</option>
		                {deallocationRemarkOptions.map((value) => (
	                  <option key={value} value={value}>{value}</option>
	                ))}
	              </Form.Select>
	            </Col>
          </Row>
        </Modal.Body>
	        <Modal.Footer>
	          <Button variant="secondary" onClick={() => setShowDeallocate(false)}>Cancel</Button>
	          <Button variant="outline-danger" onClick={submitDeallocate} disabled={!String(deallocateForm.requestId || '').trim()}>
	            {canApprove ? 'Deallocate' : 'Submit for Approval'}
	          </Button>
	        </Modal.Footer>
      </Modal>

      {/* Deactivate modal */}
      <Modal show={showDeactivate} onHide={() => setShowDeactivate(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Deactivate Asset</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted small mb-2">
            Asset: <span className="fw-semibold">{activeAsset?.serial_number}</span>
          </div>
	          <Row className="g-3">
	            <Col md={6}>
	              <Form.Label>Deactivation Date *</Form.Label>
	              <Form.Control
	                type="date"
	                value={deactivateForm.deactivationDate}
	                onChange={(e) => setDeactivateForm({ ...deactivateForm, deactivationDate: e.target.value })}
	              />
	            </Col>
              <Col md={6}>
                <Form.Label>Deactivation Reason *</Form.Label>
                <Form.Select
                  value={deactivateForm.deactivationReason}
                  onChange={(e) => setDeactivateForm({ ...deactivateForm, deactivationReason: e.target.value })}
                >
                  <option value="">Select</option>
                  {deactivationReasonOptions.map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </Form.Select>
              </Col>
	            <Col md={12}>
	              <div />
	            </Col>
          </Row>
        </Modal.Body>
	        <Modal.Footer>
	          <Button variant="secondary" onClick={() => setShowDeactivate(false)}>Cancel</Button>
		          <Button
		            variant="warning"
		            onClick={submitDeactivate}
		            disabled={!canApprove}
		          >
		            Deactivate
		          </Button>
	        </Modal.Footer>
      </Modal>

      {/* My requests modal */}
      <Modal show={showMyRequests} onHide={() => setShowMyRequests(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>My Requests</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {myRequestsLoading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : (
            <Table responsive bordered hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Type</th>
	                  <th>Serial Number</th>
                  <th>Status</th>
                  <th>Requested At</th>
                  <th>Approved At</th>
                </tr>
              </thead>
              <tbody>
                {(myRequestRows || []).map((row) => (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>{row.request_type}</td>
	                    <td>{row.serial_number || '-'}</td>
                    <td>{row.status}</td>
                    <td>{formatDate(row.requested_at)}</td>
                    <td>{formatDate(row.approved_at)}</td>
                  </tr>
                ))}
                {!myRequestRows?.length && (
                  <tr><td colSpan={6} className="text-center text-muted py-3">No requests yet.</td></tr>
                )}
              </tbody>
            </Table>
          )}
        </Modal.Body>
      </Modal>

    </Container>
  );
};

export default ITAssetManagementPage;

ITAssetManagementPage.propTypes = {
  initialTab: PropTypes.oneOf(['my', 'master', 'inventory', 'history', 'user_history', 'audit'])
};
